import { EditableText, SlotBlocks, defineBlock } from "eddev/blocks"
import { Chip } from "@components/atoms/Chip"
import * as Icons from "@components/atoms/Icons"
import { useSubtheme } from "@hooks/useSubtheme"
import { twMerge } from "tailwind-merge"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Link } from "eddev/routing"
import { Logo } from "@components/atoms/Logo"
import { Fragment, ReactNode, useEffect, useRef, useState } from "react"
import { kebabCase } from "@utils/kebabCase"
import { motion } from "framer-motion"
import { DistractionChart } from "@components/atoms/DistractionChart"
import { useToolSubmission } from "@hooks/useToolSubmission"
import { useQueryParams } from "@hooks/useQueryParams"
import { getToolResult } from "@utils/rsrhSubmissions"

export const meta: BlockMeta = {
  title: "Driving Distractibility Tool",
  category: "common",
  icon: "car",
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post"],
  },
}

// A content block that collapses behind a toggle on mobile, and is always shown as a plain
// content div from md upward (the caret + collapse behaviour only apply below that breakpoint).
export const MobileAccordion = ({ title, children }: { title: string; children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="type-label-caps text-mid-grey flex items-center justify-between"
      >
        <span>{title}</span>
        <i className={twMerge("md:hidden aaa-animate", isOpen && "rotate-180")}>{Icons["CARET_DOWN"]}</i>
      </button>
      <div className={twMerge("w-full overflow-hidden md:h-auto", isOpen ? "h-auto" : "h-0")}>{children}</div>
    </div>
  )
}

export default defineBlock("rsrh/driving-distractibility-tool", (props) => {
  useSubtheme("grey")

  const [page, setPage] = useState(PAGE_HOME)
  const [taskMode, setTaskMode] = useState<"common" | "custom" | null>(null)
  const [customTasks, setCustomTasks] = useState<string[]>([""])
  const [isParkedConfirmed, setIsParkedConfirmed] = useState(false)
  const [phoneConnected, setPhoneConnected] = useState<boolean | null>(null)
  const [activeSlide, setActiveSlide] = useState(0)
  const [taskCounters, setTaskCounters] = useState<Record<number, string[]>>({})
  const [currentTaskStep, setCurrentTaskStep] = useState<1 | 2>(1)
  const [taskQuestionAnswers, setTaskQuestionAnswers] = useState<Record<number, string[]>>({})
  const [noteVisible, setNoteVisible] = useState<Record<number, boolean>>({})
  const [taskNotes, setTaskNotes] = useState<Record<number, string>>({})
  const [isExporting, setIsExporting] = useState(false)
  const summaryRef = useRef<HTMLDivElement>(null)

  // A "view a shared result" link (?id=<sessionId>) loads a previously-submitted session
  // read-only: it populates this same state from Airtable, then jumps straight to the summary
  // page. There's no path back into the interactive counters/questions for that loaded data —
  // "Test another car" starts a genuinely fresh session rather than editing the shared one.
  const [queryParams, updateQueryParams] = useQueryParams()
  const sharedSessionId = queryParams.get("id")
  const isSharedView = !!sharedSessionId
  const [sharedResultStatus, setSharedResultStatus] = useState<"idle" | "loading" | "loaded" | "not-found" | "error">(
    "idle",
  )

  useEffect(() => {
    if (!sharedSessionId) return
    setSharedResultStatus("loading")

    getToolResult("driving-distractibility", sharedSessionId)
      .then((result) => {
        if (!result.found) {
          setSharedResultStatus("not-found")
          return
        }

        const fields = result.fields
        const items = [...result.items].sort((a, b) => (Number(a["Task Index"]) || 0) - (Number(b["Task Index"]) || 0))

        const loadedTaskMode: "common" | "custom" = fields["Task Mode"] === "custom" ? "custom" : "common"
        const loadedPhoneConnected =
          fields["Phone Connected"] === "Yes" ? true : fields["Phone Connected"] === "No" ? false : null

        setTaskMode(loadedTaskMode)
        setPhoneConnected(loadedPhoneConnected)
        setIsParkedConfirmed(!!fields["Is Parked Confirmed"])

        if (loadedTaskMode === "custom") {
          setCustomTasks(items.map((item) => String(item["Task Name"] ?? "")))
        }

        const counters: Record<number, string[]> = {}
        const questionAnswers: Record<number, string[]> = {}
        const notes: Record<number, string> = {}

        items.forEach((item) => {
          const index = (Number(item["Task Index"]) || 1) - 1
          try {
            counters[index] = JSON.parse(String(item["Interaction Log"] ?? "[]"))
          } catch {
            counters[index] = []
          }
          questionAnswers[index] = String(item["Flagged Difficulties"] ?? "")
            .split("; ")
            .filter(Boolean)
          notes[index] = String(item["Note"] ?? "")
        })

        setTaskCounters(counters)
        setTaskQuestionAnswers(questionAnswers)
        setTaskNotes(notes)
        setSharedResultStatus("loaded")
      })
      .catch(() => setSharedResultStatus("error"))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedSessionId])

  const SLIDE_COUNT = 3

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [page, currentTaskStep])

  useEffect(() => {
    const id = setInterval(() => setActiveSlide((s) => (s + 1) % SLIDE_COUNT), 5000)
    return () => clearInterval(id)
  }, [activeSlide])

  // Phone-connected sessions test the phone-dependent tasks (calls, messages, nav, music);
  // phone-disconnected sessions test the car's built-in controls instead.
  const commonTaskSet = phoneConnected === false ? DISCONNECTED_TASKS : COMMON_TASKS
  const activeTasks = taskMode === "custom" ? customTasks : commonTaskSet.map((t) => t.task)
  const PAGE_SUMMARY = QUESTIONS_START + activeTasks.length

  // Once the shared-result data above has landed in state, activeTasks/PAGE_SUMMARY on THIS
  // render now reflect it correctly — safe to jump straight to the summary page.
  useEffect(() => {
    if (sharedResultStatus === "loaded") {
      setPage(PAGE_SUMMARY)
    }
  }, [sharedResultStatus, PAGE_SUMMARY])

  // Every page that shows the dot indicator: SETUP_1 through the last question (excludes home, welcome, and summary).
  const PAGINATION_PAGES = Array.from({ length: PAGE_SUMMARY - SETUP_1 }, (_, i) => SETUP_1 + i)

  const isQuestionPage = page >= QUESTIONS_START && page < PAGE_SUMMARY
  const currentTaskIndex = page - QUESTIONS_START
  const currentTask = isQuestionPage ? activeTasks[currentTaskIndex] : null

  const counterLog = taskCounters[currentTaskIndex] ?? []
  const totalCounterCount = counterLog.length
  const counterCounts = counterLog.reduce<Record<string, number>>((counts, type) => {
    counts[type] = (counts[type] ?? 0) + 1
    return counts
  }, {})

  const {
    row: distractionRow,
    stars: distractionStars,
    label: distractionLabel,
    starFill: distractionStarFill,
    textFill: distractionTextFill,
    eyesOffRoadSeconds,
    drivingDistanceMeters,
  } = getDistractionForSteps(totalCounterCount)

  const averageSteps = activeTasks.length
    ? Math.round(activeTasks.reduce((sum, _, i) => sum + (taskCounters[i]?.length ?? 0), 0) / activeTasks.length)
    : 0
  const { starFill: averageStarFill, textFill: averageTextFill } = getDistractionForSteps(averageSteps)

  const totalEyesOffRoadSeconds = activeTasks.reduce(
    (sum, _, i) => sum + getDistractionForSteps(taskCounters[i]?.length ?? 0).eyesOffRoadSeconds,
    0,
  )
  const totalDrivingDistanceMeters = activeTasks.reduce(
    (sum, _, i) => sum + getDistractionForSteps(taskCounters[i]?.length ?? 0).drivingDistanceMeters,
    0,
  )

  // For each counter type, the % of a task's steps it made up, averaged across every task.
  const getAverageCounterPercentage = (type: string) => {
    if (!activeTasks.length) return 0
    const percentages = activeTasks.map((_, i) => {
      const log = taskCounters[i] ?? []
      if (!log.length) return 0
      return (log.filter((entry) => entry === type).length / log.length) * 100
    })
    return percentages.reduce((sum, percentage) => sum + percentage, 0) / percentages.length
  }

  // Highest step count among this session's tasks — tasks tied for this get the "most steps"
  // callout on the results page (as long as they're not already a 5-star task).
  const maxTaskSteps = activeTasks.length ? Math.max(...activeTasks.map((_, i) => taskCounters[i]?.length ?? 0)) : 0

  // Maps each flagged difficulty (a TASK_QUESTIONS issue) to the index of the first task it was
  // selected on, so its recommendation/tip is shown once on the results page rather than
  // repeated on every task where it was flagged.
  const firstFlaggedIssueTaskIndex: Record<string, number> = {}
  activeTasks.forEach((_, i) => {
    for (const issue of taskQuestionAnswers[i] ?? []) {
      if (!(issue in firstFlaggedIssueTaskIndex)) {
        firstFlaggedIssueTaskIndex[issue] = i
      }
    }
  })

  const logCounter = (type: string) =>
    setTaskCounters((prev) => ({ ...prev, [currentTaskIndex]: [...(prev[currentTaskIndex] ?? []), type] }))

  const undoCounter = () =>
    setTaskCounters((prev) => {
      const log = prev[currentTaskIndex] ?? []
      if (!log.length) return prev
      return { ...prev, [currentTaskIndex]: log.slice(0, -1) }
    })

  const selectedQuestions = taskQuestionAnswers[currentTaskIndex] ?? []
  const isNoteVisible = noteVisible[currentTaskIndex] ?? false
  const noteText = taskNotes[currentTaskIndex] ?? ""

  const toggleQuestion = (question: string) =>
    setTaskQuestionAnswers((prev) => {
      const current = prev[currentTaskIndex] ?? []
      const next = current.includes(question) ? current.filter((q) => q !== question) : [...current, question]
      return { ...prev, [currentTaskIndex]: next }
    })

  const toggleNote = () =>
    setNoteVisible((prev) => ({ ...prev, [currentTaskIndex]: !(prev[currentTaskIndex] ?? false) }))

  const updateNoteText = (value: string) => setTaskNotes((prev) => ({ ...prev, [currentTaskIndex]: value }))

  const goNext = () => {
    // Skip the custom-tasks input page (SETUP_3) entirely when the user is using the common task list.
    if (page === SETUP_2 && taskMode !== "custom") {
      setPage(INSTRUCTIONS)
      return
    }
    setPage((p) => Math.min(p + 1, PAGE_SUMMARY))
  }
  const goBack = () => {
    if (page === INSTRUCTIONS && taskMode !== "custom") {
      setPage(SETUP_2)
      return
    }
    setPage((p) => Math.max(p - 1, PAGE_HOME))
  }
  const restart = () => {
    setPage(PAGE_HOME)
    setTaskMode(null)
    setCustomTasks([""])
    setIsParkedConfirmed(false)
    setPhoneConnected(null)
    setActiveSlide(0)
    setTaskCounters({})
    setCurrentTaskStep(1)
    setTaskQuestionAnswers({})
    setNoteVisible({})
    setTaskNotes({})
    resetSubmission()
    setSharedResultStatus("idle")
    updateQueryParams({ id: null })
  }

  const exportSummary = async () => {
    if (!summaryRef.current || isExporting) return
    setIsExporting(true)
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")])
      const canvas = await html2canvas(summaryRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        // Skip the Download/Email/Share row and the "Test another car" button — action
        // chrome that shouldn't appear in the exported document.
        ignoreElements: (element) => element.hasAttribute("data-pdf-hide"),
      })

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const contentWidth = pageWidth - PDF_MARGIN_MM * 2
      const contentHeight = pageHeight - PDF_MARGIN_MM * 2

      // Slice the canvas into per-page chunks (rather than reusing one tall image at a shifting
      // offset) so the margin is respected at both the top and bottom of every page, not just the top.
      const pxPerMm = canvas.width / contentWidth
      const pageHeightPx = Math.floor(contentHeight * pxPerMm)

      let renderedHeightPx = 0
      let isFirstPage = true

      while (renderedHeightPx < canvas.height) {
        const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedHeightPx)

        const pageCanvas = document.createElement("canvas")
        pageCanvas.width = canvas.width
        pageCanvas.height = sliceHeightPx
        pageCanvas
          .getContext("2d")
          ?.drawImage(canvas, 0, renderedHeightPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx)

        if (!isFirstPage) pdf.addPage()
        pdf.addImage(
          pageCanvas.toDataURL("image/png"),
          "PNG",
          PDF_MARGIN_MM,
          PDF_MARGIN_MM,
          contentWidth,
          sliceHeightPx / pxPerMm,
        )

        renderedHeightPx += sliceHeightPx
        isFirstPage = false
      }

      pdf.save("driving-distractibility-summary.pdf")
    } finally {
      setIsExporting(false)
    }
  }

  const { resetSubmission, sessionId } = useToolSubmission(
    "driving-distractibility",
    page === PAGE_SUMMARY && !isSharedView,
    () => ({
      fields: {
        "Task Mode": taskMode ?? "common",
        "Is Parked Confirmed": isParkedConfirmed,
        "Phone Connected": phoneConnected === null ? null : phoneConnected ? "Yes" : "No",
        "Task Count": activeTasks.length,
        "Average Steps": averageSteps,
        "Average Eyes Off Road Seconds": activeTasks.length ? totalEyesOffRoadSeconds / activeTasks.length : 0,
        "Average Driving Distance Meters": activeTasks.length ? totalDrivingDistanceMeters / activeTasks.length : 0,
      },
      items: activeTasks.map((task, i) => {
        const log = taskCounters[i] ?? []
        const { label, stars, eyesOffRoadSeconds, drivingDistanceMeters } = getDistractionForSteps(log.length)
        return {
          "Task Index": i + 1,
          "Task Name": task,
          Steps: log.length,
          "Distraction Label": label,
          Stars: stars,
          "Eyes Off Road Seconds": eyesOffRoadSeconds,
          "Driving Distance Meters": drivingDistanceMeters,
          "Flagged Difficulties": (taskQuestionAnswers[i] ?? []).join("; "),
          Note: taskNotes[i] ?? "",
          "Interaction Log": JSON.stringify(log),
        }
      }),
    }),
  )

  // The link a viewer opens is always the *original* session's id — when already viewing a
  // shared result, re-sharing points at the same result rather than the (irrelevant) live one.
  const shareSessionId = isSharedView ? sharedSessionId : sessionId
  const [isLinkCopied, setIsLinkCopied] = useState(false)

  const copyShareLink = async () => {
    const url = new URL(window.location.href)
    url.search = ""
    url.searchParams.set("id", shareSessionId)

    try {
      await navigator.clipboard.writeText(url.toString())
      setIsLinkCopied(true)
      setTimeout(() => setIsLinkCopied(false), 8000)
    } catch (err) {
      console.error("[driving-distractibility-tool] failed to copy share link", err)
    }
  }

  const customTasksValid = customTasks.every((task) => task.trim().length > 0)

  const paginationDots = (
    <div className="flex items-center gap-2 flex-wrap justify-center">
      {PAGINATION_PAGES.map((dotPage) => (
        <span
          key={dotPage}
          className={twMerge("w-2 h-2 rounded-full aaa-animate", page === dotPage ? "bg-black" : "bg-mid-grey/30")}
        />
      ))}
    </div>
  )

  // Builds a stepped keyframe timeline for the task ticker: hold, swipe up one item (ease-out), hold, swipe...
  // The content is rendered twice back to back, so ending at -50% loops seamlessly into the start.
  const tickerHoldSeconds = 2
  const tickerSwipeSeconds = 0.4
  const tickerY: string[] = ["0%"]
  const tickerTimes: number[] = [0]
  const tickerEase: string[] = []
  let tickerElapsed = 0
  for (let i = 0; i < commonTaskSet.length; i++) {
    tickerElapsed += tickerHoldSeconds
    tickerY.push(tickerY[tickerY.length - 1])
    tickerTimes.push(tickerElapsed)
    tickerEase.push("linear")

    tickerElapsed += tickerSwipeSeconds
    tickerY.push(`${-(((i + 1) / commonTaskSet.length) * 50)}%`)
    tickerTimes.push(tickerElapsed)
    tickerEase.push("easeOut")
  }
  const tickerDuration = tickerElapsed
  const tickerTimesNormalized = tickerTimes.map((t) => t / tickerDuration)

  return (
    <div
      className={twMerge(
        "px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] flex flex-col gap-4 md:gap-8 pt-6 pb-8 items-center mx-auto",
        env.admin && "bgLight-grey",
      )}
    >
      {isSharedView && sharedResultStatus !== "loaded" ? (
        <div className="w-full flex flex-col items-center justify-center gap-4 py-24 bg-white rounded-md">
          {sharedResultStatus === "not-found" || sharedResultStatus === "error" ? (
            <>
              <h2 className="type-title-m">We couldn't find this result</h2>
              <p className="type-body-s text-mid-grey text-center max-w-[400px]">
                This link may have expired, or the result may no longer be available.
              </p>
            </>
          ) : (
            <p className="type-body-m py-8 text-mid-grey">Loading results...</p>
          )}
        </div>
      ) : (
        <>
          {page === PAGE_HOME && (
            <>
              <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
                <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-start gap-6 p-6">
                  <div className="w-full flex flex-col gap-6 justify-start items-start">
                    <Chip color="outline">Interactive Tool</Chip>
                    <EditableText
                      as="h1"
                      store="title"
                      placeholder="Enter title..."
                      defaultValue="Driving Distractibility Tool"
                      className="type-title-xl"
                    />
                  </div>
                  <div className="w-full flex flex-col gap-6 justify-start items-start">
                    <EditableText as="p" store="content" placeholder="Enter content..." className="type-body-m" />
                    <button className="!hidden md:!flex button-yellow" onClick={() => setPage(WELCOME)}>
                      <span>Get started (~7mins)</span>
                      <i>{Icons["ARROW_RIGHT"]}</i>
                    </button>
                  </div>
                </div>
                <div
                  className={twMerge(
                    "col-span-12 md:col-span-6 relative flex aspect-[10/7] bg-white md:bg-light-white",
                    "[&>div]:!absolute [&>div]:!w-[calc(100%-(var(--spacing-4)*2))] [&>div]:!h-[calc(100%-(var(--spacing-4)*2))] [&>div]:!top-4 [&>div]:!left-4",
                    "md:[&>div]:!w-full md:[&>div]:!h-full md:[&>div]:!top-0 md:[&>div]:!left-0",
                    "[&_figure]:!absolute [&_figure]:!w-[calc(100%-(var(--spacing-4)*2))] [&_figure]:!h-[calc(100%-(var(--spacing-4)*2))] [&_figure]:!top-4 [&_figure]:!left-4 [&_figure]:rounded-md [&_figure]:overflow-hidden [&_figure]:!m-0",
                    "md:[&_figure]:!w-full md:[&_figure]:!h-full md:[&_figure]:!top-0 md:[&_figure]:!left-0 md:[&_figure]:rounded-none",
                    "[&_figure>div]:!absolute [&_figure>div]:!w-full [&_figure>div]:!h-full [&_figure>div]:!top-0 [&_figure>div]:!left-0",
                    "[&_figure>img]:!absolute [&_figure>img]:!m-0 [&_figure>img]:!w-full [&_figure>img]:!h-full [&_figure>img]:!top-0 [&_figure>img]:!left-0 [&_figure>img]:!rounded-none [&_figure>img]:!object-cover",
                  )}
                >
                  <SlotBlocks
                    id="image"
                    allowedBlocks={["core/image"]}
                    template={[["core/image", {}]]}
                    templateLock="all"
                  />
                </div>
              </div>

              <div className="md:hidden grid-auto !w-full">
                <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
                  <button className="button-yellow w-full box-border" onClick={() => setPage(WELCOME)}>
                    <span>Get started (~7mins)</span>
                    <i>{Icons["ARROW_RIGHT"]}</i>
                  </button>
                </div>
              </div>
            </>
          )}

          {page === WELCOME && (
            <>
              <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
                <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-start gap-6 p-6">
                  <span className="type-label-caps text-mid-grey">Welcome</span>

                  <div className="flex flex-col gap-4">
                    <EditableText as="p" store="intro_1_title" placeholder="Intro title..." className="type-title-s" />
                    <EditableText
                      as="p"
                      store="intro_1_content"
                      placeholder="Intro content..."
                      className="type-body-s"
                    />
                  </div>

                  <div className="!hidden md:!flex flex-col gap-4">
                    <div className="flex gap-3 items-center">
                      <input
                        type="checkbox"
                        id="parked-confirm-desktop"
                        checked={isParkedConfirmed}
                        onChange={(e) => setIsParkedConfirmed(e.target.checked)}
                        className="sr-only"
                      />
                      <label
                        htmlFor="parked-confirm-desktop"
                        className={twMerge(
                          "w-6 h-6 md:w-4 md:h-4 flex-shrink-0 border border-mid-grey rounded-[2px] cursor-pointer aaa-animate [&_svg]:w-full [&_svg]:h-full [&_svg]:scale-150",
                          isParkedConfirmed && "bg-yellow border-yellow",
                        )}
                      >
                        {isParkedConfirmed && Icons["TICK"]}
                      </label>
                      <label htmlFor="parked-confirm-desktop" className="type-label-caps text-mid-grey cursor-pointer">
                        I confirm my vehicle is safely parked.
                      </label>
                    </div>

                    <button
                      className="button-yellow disabled:button-disabled"
                      onClick={goNext}
                      disabled={!isParkedConfirmed}
                    >
                      <span>I'm parked, lets get started</span>
                      <i>{Icons["ARROW_RIGHT"]}</i>
                    </button>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6 relative flex flex-col justify-center md:aspect-[10/7] bg-light-white p-8">
                  <div className="flex flex-col gap-4">
                    <div className="w-full my-6 border border-solid border-grey rounded-md overflow-hidden [&_canvas]:scale-105 [&_canvas]:origin-bottom">
                      <DistractionChart showCard={false} showYAxis={false} showTitle={false} largeTime short />
                    </div>

                    <EditableText
                      as="span"
                      store="intro_1_subtitle"
                      placeholder="Subtitle..."
                      className="type-label-caps text-mid-grey"
                    />
                    <EditableText as="p" store="intro_1_subcontent" placeholder="Content..." className="type-body-s" />
                  </div>
                </div>
              </div>

              <div className="md:hidden grid-auto !w-full">
                <div className="col-span-12 bg-white p-4 rounded-md flex flex-col gap-4 justify-between">
                  <div className="flex gap-3 items-center">
                    <input
                      type="checkbox"
                      id="parked-confirm-mobile"
                      checked={isParkedConfirmed}
                      onChange={(e) => setIsParkedConfirmed(e.target.checked)}
                      className="sr-only"
                    />
                    <label
                      htmlFor="parked-confirm-mobile"
                      className={twMerge(
                        "w-6 h-6 md:w-4 md:h-4 flex-shrink-0 border border-mid-grey rounded-[2px] cursor-pointer aaa-animate [&_svg]:w-full [&_svg]:h-full [&_svg]:scale-150",
                        isParkedConfirmed && "bg-yellow border-yellow",
                      )}
                    >
                      {isParkedConfirmed && Icons["TICK"]}
                    </label>
                    <label htmlFor="parked-confirm-mobile" className="type-label-caps text-mid-grey cursor-pointer">
                      I confirm my vehicle is safely parked.
                    </label>
                  </div>

                  <button
                    className="button-yellow disabled:button-disabled w-full box-border"
                    onClick={goNext}
                    disabled={!isParkedConfirmed}
                  >
                    <span>I'm parked, lets get started</span>
                    <i>{Icons["ARROW_RIGHT"]}</i>
                  </button>
                </div>
              </div>
            </>
          )}

          {page === SETUP_1 && (
            <>
              <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
                <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-start gap-6 p-6">
                  <span className="type-label-caps text-mid-grey">Setup</span>

                  <div className="w-full flex flex-col gap-4">
                    <h3 className="!type-title-s !text-center">Is your phone connected to the car?</h3>

                    <div className="grid grid-cols-2 w-full gap-3 md:gap-2 my-4">
                      <button
                        onClick={() => setPhoneConnected(true)}
                        className={twMerge(
                          "flex flex-col justify-between items-center text-center rounded-sm bg-light-grey p-4 gap-4 aaa-animate",
                          phoneConnected === true && "bg-yellow",
                        )}
                      >
                        <div className="relative flex items-center justify-center w-full h-[60px] overflow-hidden">
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M24 35.3684H24.0189"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M33.4737 5.05264H14.5263C12.4334 5.05264 10.7368 6.74925 10.7368 8.84212V39.1579C10.7368 41.2508 12.4334 42.9474 14.5263 42.9474H33.4737C35.5665 42.9474 37.2631 41.2508 37.2631 39.1579V8.84212C37.2631 6.74925 35.5665 5.05264 33.4737 5.05264Z"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M37.8952 25.0543C41.9646 25.0543 45.2636 21.7553 45.2636 17.6859C45.2636 13.6164 41.9646 10.3174 37.8952 10.3174C33.8257 10.3174 30.5267 13.6164 30.5267 17.6859C30.5267 21.7553 33.8257 25.0543 37.8952 25.0543Z"
                              fill="#F3F3F3"
                              stroke="black"
                              strokeWidth="2.33333"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M35.6842 17.6826L37.1579 19.1563L40.1053 16.2089"
                              stroke="black"
                              strokeWidth="2.33333"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <span className="type-label-caps">Yes, my phone is connected</span>
                      </button>

                      <button
                        onClick={() => setPhoneConnected(false)}
                        className={twMerge(
                          "flex flex-col justify-between items-center text-center rounded-sm bg-light-grey p-4 gap-4 aaa-animate",
                          phoneConnected === false && "bg-yellow",
                        )}
                      >
                        <div className="relative flex items-center justify-center w-full h-[60px] overflow-hidden">
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M37.0522 12.2121L42.1049 7.15952"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8.42139 40.8404L13.474 35.7878"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M15.6635 37.9789C16.039 38.3558 16.4853 38.6548 16.9766 38.8588C17.4679 39.0628 17.9947 39.1678 18.5267 39.1678C19.0587 39.1678 19.5854 39.0628 20.0768 38.8588C20.5681 38.6548 21.0143 38.3558 21.3898 37.9789L25.2635 34.1053L15.1583 24L11.2846 27.8737C10.9077 28.2492 10.6087 28.6954 10.4047 29.1868C10.2007 29.6781 10.0957 30.2048 10.0957 30.7368C10.0957 31.2688 10.2007 31.7956 10.4047 32.2869C10.6087 32.7783 10.9077 33.2245 11.2846 33.6L15.6635 37.9789Z"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M17.6842 26.5279L21.8947 22.3174"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M22.7368 31.5806L26.9473 27.3701"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M25.2632 13.8947L35.3684 24L39.2421 20.1263C39.619 19.7508 39.918 19.3045 40.122 18.8132C40.326 18.3219 40.431 17.7951 40.431 17.2631C40.431 16.7311 40.326 16.2044 40.122 15.713C39.918 15.2217 39.619 14.7755 39.2421 14.4L34.8632 10.021C34.4877 9.64419 34.0414 9.3452 33.5501 9.14118C33.0588 8.93717 32.532 8.83215 32 8.83215C31.468 8.83215 30.9413 8.93717 30.4499 9.14118C29.9586 9.3452 29.5124 9.64419 29.1369 10.021L25.2632 13.8947Z"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <span className="type-label-caps">Continue without my phone</span>
                      </button>
                    </div>
                  </div>

                  <div className="w-full hidden md:flex items-center justify-between">
                    <button className="button-grey" onClick={goBack}>
                      <i>{Icons["ARROW_LEFT"]}</i>
                      <span>Back</span>
                    </button>

                    {paginationDots}

                    <button
                      className="button-yellow disabled:button-disabled"
                      onClick={goNext}
                      disabled={phoneConnected === null}
                    >
                      <span>Next</span>
                      <i>{Icons["ARROW_RIGHT"]}</i>
                    </button>
                  </div>
                </div>

                <div className="col-span-12 md:col-span-6 relative flex flex-col justify-center md:aspect-[10/7] bg-light-white p-8">
                  <MobileAccordion title="How to Connect your phone">
                    <p className="type-body-s">
                      Connecting your phone lets you complete the full test, including calls, messages and music. You
                      can also continue without it and test the car's built-in controls only.
                    </p>

                    <div className="flex flex-col mt-6 gap-4">
                      {HOW_TO.map((item, i) => (
                        <div className="flex gap-3 items-center" key={i}>
                          <span className="flex items-center justify-center type-label-m w-[32px] h-[32px] bg-yellow rounded-full">
                            {i + 1}
                          </span>
                          <span className="type-label-m">{item}</span>
                        </div>
                      ))}
                    </div>
                  </MobileAccordion>
                </div>
              </div>

              <div className="md:hidden grid-auto !w-full">
                <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
                  <button className="button-grey" onClick={goBack}>
                    <i>{Icons["ARROW_LEFT"]}</i>
                    <span>Back</span>
                  </button>

                  <button
                    className="button-yellow disabled:button-disabled"
                    onClick={goNext}
                    disabled={phoneConnected === null}
                  >
                    <span>{page === PAGE_SUMMARY - 1 ? "See summary" : "Next"}</span>
                    <i>{Icons["ARROW_RIGHT"]}</i>
                  </button>
                </div>
              </div>
            </>
          )}

          {page === SETUP_2 && (
            <>
              <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
                <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-start gap-6 p-6">
                  <span className="type-label-caps text-mid-grey">Setup</span>

                  <div className="w-full flex flex-col gap-4">
                    <h3 className="!type-title-s !text-center">What would you like to test?</h3>

                    <div className="grid grid-cols-2 w-full gap-3 md:gap-2 my-4">
                      <button
                        onClick={() => setTaskMode("custom")}
                        className={twMerge(
                          "flex flex-col justify-between items-center text-center rounded-sm bg-light-grey p-4 gap-4 aaa-animate",
                          taskMode === "custom" && "bg-yellow",
                        )}
                      >
                        <div className="relative flex items-center justify-center w-full h-[60px] overflow-hidden">
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M36.8513 17.4472C37.5191 16.7795 37.8944 15.8739 37.8945 14.9295C37.8946 13.9852 37.5196 13.0795 36.8519 12.4116C36.1843 11.7438 35.2786 11.3685 34.3343 11.3684C33.3899 11.3683 32.4842 11.7433 31.8163 12.411L14.9582 29.2729C14.665 29.5653 14.4481 29.9254 14.3267 30.3213L12.658 35.8186C12.6254 35.9278 12.6229 36.0439 12.6509 36.1544C12.6789 36.2649 12.7363 36.3658 12.8169 36.4464C12.8976 36.5269 12.9986 36.5841 13.1092 36.6119C13.2198 36.6397 13.3358 36.6371 13.445 36.6043L18.9435 34.9369C19.3391 34.8166 19.6991 34.601 19.9919 34.3091L36.8513 17.4472Z"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M29.0527 15.1579L34.1054 20.2105"
                              stroke="black"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <span className="type-label-caps">Your own custom tasks</span>
                      </button>

                      <button
                        onClick={() => setTaskMode("common")}
                        className={twMerge(
                          "flex flex-col justify-between items-center text-center rounded-sm bg-light-grey p-4 gap-4 aaa-animate",
                          taskMode === "common" && "bg-yellow",
                        )}
                      >
                        <div className="relative flex items-center justify-center w-full h-[60px] overflow-hidden">
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M30.8002 8.0258L32.4269 8.2284C36.1816 8.69602 39 11.8872 39 15.6709V36.6497C39 40.3235 36.2635 43.422 32.6179 43.876C26.5627 44.6302 20.4373 44.6302 14.3821 43.876C10.7365 43.422 8 40.3235 8 36.6497V15.6709C8 11.8872 10.8184 8.69602 14.5731 8.2284L16.1998 8.0258C16.8442 5.70411 18.9731 4 21.5 4H25.5C28.0269 4 30.1558 5.70411 30.8002 8.0258ZM16 11.0739L14.9439 11.2054C12.691 11.486 11 13.4007 11 15.6709V36.6497C11 38.8101 12.6092 40.632 14.7529 40.899C20.5619 41.6225 26.4381 41.6225 32.2471 40.899C34.3908 40.632 36 38.8101 36 36.6497V15.6709C36 13.4007 34.309 11.486 32.0561 11.2054L31 11.0739V13.5C31 14.3284 30.3284 15 29.5 15H17.5C16.6716 15 16 14.3284 16 13.5V11.0739ZM19 9.5C19 8.11929 20.1193 7 21.5 7H25.5C26.8807 7 28 8.11929 28 9.5V12H19V9.5Z"
                              fill="#222222"
                            />
                            <path
                              d="M31 23C31 22.1716 30.3284 21.5 29.5 21.5H17.5C16.6716 21.5 16 22.1716 16 23C16 23.8284 16.6716 24.5 17.5 24.5H29.5C30.3284 24.5 31 23.8284 31 23Z"
                              fill="#222222"
                            />
                            <path
                              d="M29 29C29 28.1716 28.3284 27.5 27.5 27.5H17.5C16.6716 27.5 16 28.1716 16 29C16 29.8284 16.6716 30.5 17.5 30.5H27.5C28.3284 30.5 29 29.8284 29 29Z"
                              fill="#222222"
                            />
                          </svg>
                        </div>
                        <span className="type-label-caps">Five common tasks</span>
                      </button>
                    </div>
                  </div>

                  <div className="w-full hidden md:flex items-center justify-between">
                    <button className="button-grey" onClick={goBack}>
                      <i>{Icons["ARROW_LEFT"]}</i>
                      <span>Back</span>
                    </button>

                    {paginationDots}

                    <button
                      className="button-yellow disabled:button-disabled"
                      onClick={goNext}
                      disabled={taskMode === null}
                    >
                      <span>Next</span>
                      <i>{Icons["ARROW_RIGHT"]}</i>
                    </button>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6 relative flex flex-col justify-center md:aspect-[10/7] bg-light-white p-8">
                  <MobileAccordion title="Our five common tasks">
                    <p className="type-body-s">
                      These five everyday tasks are the same ones used in real testing to measure how much a car's
                      technology pulls your attention off the road. Together, they are designed to cover the full range
                      of tasks from quick and simple to genuinely demanding.
                    </p>

                    <div className="flex flex-col mt-6 gap-4">
                      {commonTaskSet.map((item, i) => (
                        <div className="flex gap-3 items-center" key={i}>
                          <span className="flex items-center justify-center type-label-l w-[32px] h-[32px] bg-yellow rounded-full">
                            {item.icon}
                          </span>
                          <span className="type-label-m">{item.task}</span>
                        </div>
                      ))}
                    </div>
                  </MobileAccordion>
                </div>
              </div>

              <div className="md:hidden grid-auto !w-full">
                <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
                  <button className="button-grey" onClick={goBack}>
                    <i>{Icons["ARROW_LEFT"]}</i>
                    <span>Back</span>
                  </button>

                  <button
                    className="button-yellow disabled:button-disabled"
                    onClick={goNext}
                    disabled={taskMode === null}
                  >
                    <span>{page === PAGE_SUMMARY - 1 ? "See summary" : "Next"}</span>
                    <i>{Icons["ARROW_RIGHT"]}</i>
                  </button>
                </div>
              </div>
            </>
          )}

          {page === SETUP_3 && (
            <>
              <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
                <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-start gap-6 p-6">
                  <span className="type-label-caps text-mid-grey">Setup</span>

                  <div className="w-full flex flex-col gap-4 grow">
                    <h3 className="!type-title-s !text-center">Add custom tasks</h3>
                    <div className="flex flex-col gap-4">
                      {customTasks.map((task, index) => (
                        <div key={index} className="flex flex-col gap-1">
                          <label className="type-label-caps">Task {index + 1}</label>
                          <div className="flex gap-1 items-center">
                            <input
                              type="text"
                              value={task}
                              onChange={(e) =>
                                setCustomTasks((prev) => prev.map((t, i) => (i === index ? e.target.value : t)))
                              }
                              className="w-full h-[36px] border border-solid border-grey rounded-sm px-2 type-body-s"
                            />
                            {index > 0 && (
                              <button
                                type="button"
                                aria-label={`Remove task ${index + 1}`}
                                onClick={() => setCustomTasks((prev) => prev.filter((_, i) => i !== index))}
                                className="flex-shrink-0 flex items-center justify-center h-[36px] w-[36px] rounded-sm bg-light-grey hover:bg-yellow aaa-animate"
                              >
                                <i className="w-4 h-4 flex items-center justify-center [&_svg]:w-full [&_svg]:h-full">
                                  {Icons["CLOSE"]}
                                </i>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() => setCustomTasks((prev) => [...prev, ""])}
                        className="w-auto self-end flex items-center bg-light-grey aaa-animate text-black type-label-s py-1 px-2 rounded-sm hover:bg-yellow"
                      >
                        <i className="w-4 h-4 flex items-center justify-center [&_svg]:w-full [&_svg]:h-full">
                          {Icons["ADD"]}
                        </i>
                        <span>Add another</span>
                      </button>
                    </div>
                  </div>

                  <div className="w-full hidden md:flex items-center justify-between">
                    <button className="button-grey" onClick={goBack}>
                      <i>{Icons["ARROW_LEFT"]}</i>
                      <span>Back</span>
                    </button>

                    {paginationDots}

                    <button
                      className="button-yellow disabled:button-disabled"
                      onClick={goNext}
                      disabled={!customTasksValid}
                    >
                      <span>Next</span>
                      <i>{Icons["ARROW_RIGHT"]}</i>
                    </button>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6 relative flex flex-col justify-center md:aspect-[10/7] bg-light-white p-8">
                  <MobileAccordion title="Suggested custom tasks">
                    <p className="type-body-s">
                      This custom option is for logging tasks not covered by the 5 common tasks used in the research,
                      including third-party systems like job management or dispatch apps you use as part of your work.
                      The same basic step-by-step approach applies to whatever you add. There are a few example tasks
                      below.
                    </p>

                    <div className="flex flex-col mt-6 gap-4">
                      {SUGGESTED.map((item, i) => (
                        <div className="flex gap-3 items-center" key={i}>
                          <span className="flex items-center justify-center type-label-l w-[32px] h-[32px] bg-yellow rounded-full">
                            {Icons["RESEARCH"]}
                          </span>
                          <span className="type-label-m">{item}</span>
                        </div>
                      ))}
                    </div>
                  </MobileAccordion>
                </div>
              </div>

              <div className="md:hidden grid-auto !w-full">
                <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
                  <button className="button-grey" onClick={goBack}>
                    <i>{Icons["ARROW_LEFT"]}</i>
                    <span>Back</span>
                  </button>

                  <button
                    className="button-yellow disabled:button-disabled"
                    onClick={goNext}
                    disabled={!customTasksValid}
                  >
                    <span>{page === PAGE_SUMMARY - 1 ? "See summary" : "Next"}</span>
                    <i>{Icons["ARROW_RIGHT"]}</i>
                  </button>
                </div>
              </div>
            </>
          )}

          {page === INSTRUCTIONS && (
            <>
              <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
                <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-center gap-2 p-6">
                  <span className="type-label-caps text-mid-grey"></span>

                  <div className="w-full flex flex-col gap-4 max-w-[380px]">
                    <h3 className="!type-title-s !text-center">
                      Let’s see how distracting some common tasks are in your car.
                    </h3>
                    <p className="type-body-s !text-center">
                      Counting steps helps you identify which tasks are best completed before driving. Step through each
                      tasks as you normally would, and log each interaction in parallel.
                    </p>

                    <div
                      className={twMerge(
                        "relative mt-4 mx-auto w-full max-w-[300px] h-[202px] overflow-hidden",
                        "[mask-image:linear-gradient(to_bottom,black_0%,black_25%,black_75%,transparent_100%)]",
                        "[-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_25%,black_75%,transparent_100%)]",
                      )}
                    >
                      <motion.div
                        className="flex flex-col gap-2"
                        animate={{ y: tickerY }}
                        transition={{
                          duration: tickerDuration,
                          times: tickerTimesNormalized,
                          ease: tickerEase,
                          repeat: Infinity,
                        }}
                      >
                        {[...commonTaskSet, ...commonTaskSet].map((item, i) => (
                          <div
                            key={i}
                            className="w-full flex-shrink-0 border border-solid border-light-grey p-4 flex items-center gap-4 rounded-md bg-white"
                          >
                            <i className="w-[36px] h-[36px] flex-shrink-0 bg-yellow rounded-full overflow-hidden flex items-center justify-center">
                              {item.icon}
                            </i>
                            <div className="flex flex-col gap-1">
                              <span className="type-label-m font-bold">{item.task}</span>
                            </div>
                          </div>
                        ))}
                      </motion.div>
                    </div>
                  </div>

                  <div className="w-full hidden md:flex items-center justify-between">
                    <button className="button-grey" onClick={goBack}>
                      <i>{Icons["ARROW_LEFT"]}</i>
                      <span>Back</span>
                    </button>

                    {paginationDots}

                    <button className="button-yellow disabled:button-disabled" onClick={goNext}>
                      <span>Next</span>
                      <i>{Icons["ARROW_RIGHT"]}</i>
                    </button>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6 relative flex flex-col justify-center md:aspect-[10/7] bg-light-white p-8">
                  <MobileAccordion title="How to count">
                    <p className="type-body-s">
                      Count every action you make. This includes repeated taps, button presses, voice commands,
                      commands, corrections and confirmations.
                    </p>

                    <div className="slides-container grid my-6">
                      <div
                        className={twMerge(
                          "slide col-start-1 row-start-1 flex flex-col justify-center gap-4 transition-opacity duration-500 ease-in-out",
                          activeSlide === 0 ? "opacity-100" : "opacity-0 pointer-events-none",
                        )}
                      >
                        <span className="type-label-caps">Example 1 - Navigate to a Place</span>
                        <ul className="flex flex-col gap-3">
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">You: “Hey Siri, show me to Bunnings”</span>
                            <span className="type-label-caps bg-[#A0B0EB4D] py-1 px-2 rounded-full whitespace-nowrap">
                              A voice command
                            </span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">
                              Siri: “There are three nearby, where would you like to go?”
                            </span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">You: Selecting the closest on screen</span>
                            <span className="type-label-caps bg-[#C7EDF566] py-1 px-2 rounded-full whitespace-nowrap">
                              A tap
                            </span>
                          </li>
                        </ul>
                      </div>

                      <div
                        className={twMerge(
                          "slide col-start-1 row-start-1 flex flex-col justify-center gap-4 transition-opacity duration-500 ease-in-out",
                          activeSlide === 1 ? "opacity-100" : "opacity-0 pointer-events-none",
                        )}
                      >
                        <span className="type-label-caps">Example 2 - Send a text</span>
                        <ul className="flex flex-col gap-3">
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">
                              Typing “On my way”, requires you to touch the keyboard on your screen nine times. We count
                              each one as a step.
                            </span>
                          </li>

                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">You: Press ‘O’</span>
                            <span className="type-label-caps bg-[#C7EDF566] py-1 px-2 rounded-full whitespace-nowrap">
                              A tap
                            </span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">You: Press ‘n’</span>
                            <span className="type-label-caps bg-[#C7EDF566] py-1 px-2 rounded-full whitespace-nowrap">
                              A tap
                            </span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">You: Press ‘Spacebar’</span>
                            <span className="type-label-caps bg-[#C7EDF566] py-1 px-2 rounded-full whitespace-nowrap">
                              A tap
                            </span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">...and so on.</span>
                          </li>
                        </ul>
                      </div>

                      <div
                        className={twMerge(
                          "slide col-start-1 row-start-1 flex flex-col justify-center gap-4 transition-opacity duration-500 ease-in-out",
                          activeSlide === 2 ? "opacity-100" : "opacity-0 pointer-events-none",
                        )}
                      >
                        <span className="type-label-caps">Example 3 - Send a text</span>
                        <ul className="flex flex-col gap-3">
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">You: “Hey Siri, send a message to mom”</span>
                            <span className="type-label-caps bg-[#A0B0EB4D] py-1 px-2 rounded-full whitespace-nowrap">
                              A voice command
                            </span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">Siri: “Sorry, I didn’t catch that”</span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">You: “Hey Siri, send a message to mom”</span>
                            <span className="type-label-caps bg-[#A0B0EB4D] py-1 px-2 rounded-full whitespace-nowrap">
                              A voice command
                            </span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">Siri: “What do you want the message to say”</span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">You: “On my way”</span>
                            <span className="type-label-caps bg-[#A0B0EB4D] py-1 px-2 rounded-full whitespace-nowrap">
                              A voice command
                            </span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">Siri: “Message says: On my way, ready to send?</span>
                          </li>
                          <li className="list-none flex items-center gap-4">
                            <span className="type-body-s">You: ”Yes, send.”</span>
                            <span className="type-label-caps bg-[#A0B0EB4D] py-1 px-2 rounded-full whitespace-nowrap">
                              A voice command
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 justify-center">
                      {Array.from({ length: SLIDE_COUNT }, (_, i) => (
                        <button
                          key={i}
                          type="button"
                          aria-label={`Go to slide ${i + 1}`}
                          onClick={() => setActiveSlide(i)}
                          className={twMerge(
                            "w-2 h-2 rounded-full aaa-animate",
                            activeSlide === i ? "bg-black" : "bg-mid-grey/30",
                          )}
                        />
                      ))}
                    </div>
                  </MobileAccordion>
                </div>
              </div>

              <div className="md:hidden grid-auto !w-full">
                <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
                  <button className="button-grey" onClick={goBack}>
                    <i>{Icons["ARROW_LEFT"]}</i>
                    <span>Back</span>
                  </button>

                  <button className="button-yellow disabled:button-disabled" onClick={goNext}>
                    <span>{page === PAGE_SUMMARY - 1 ? "See summary" : "Next"}</span>
                    <i>{Icons["ARROW_RIGHT"]}</i>
                  </button>
                </div>
              </div>
            </>
          )}

          {isQuestionPage && (
            <>
              {currentTaskStep === 1 && (
                <>
                  <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
                    <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-center gap-2 p-6">
                      <span className="type-label-caps text-mid-grey text-left w-full">
                        Task {currentTaskIndex + 1} of {activeTasks.length}
                      </span>

                      <div className="w-full flex flex-col gap-4">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center justify-center type-label-l w-[36px] h-[36px] mb-3 bg-yellow rounded-full self-center [&_svg]:w-3/4 [&_svg]:h-3/4">
                            {taskMode === "custom" ? Icons["RESEARCH"] : commonTaskSet[currentTaskIndex]?.icon}
                          </span>
                          <h3 className="!type-title-s !text-center">{currentTask}</h3>
                          <p className="type-body-s !text-center">Log each step as you go</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-3 md:gap-2 my-4">
                          {COUNTER_TYPES.map((counter) => (
                            <button
                              key={counter.key}
                              onClick={() => logCounter(counter.key)}
                              className={twMerge(
                                "flex flex-col justify-between items-center text-center rounded-sm bg-light-grey p-4 gap-4 aaa-animate",
                                `hover:${COUNTER_COLORS[counter.key]}`,
                              )}
                            >
                              <span className="flex items-center justify-center bg-white rounded-full h-[28px] w-[28px] type-body-m font-bold">
                                {counterCounts[counter.key] ?? 0}
                              </span>
                              <div className="relative flex items-center justify-center w-full h-[60px] overflow-hidden">
                                {counter.icon}
                              </div>
                              <span className="type-label-caps">+ {counter.label}</span>
                            </button>
                          ))}
                        </div>
                        <button
                          className="button-grey w-auto self-center disabled:button-disabled"
                          onClick={undoCounter}
                          disabled={totalCounterCount === 0}
                        >
                          Undo
                        </button>
                      </div>

                      <div className="w-full hidden md:flex items-center justify-between">
                        <button className="button-grey" onClick={goBack}>
                          <i>{Icons["ARROW_LEFT"]}</i>
                          <span>Back</span>
                        </button>

                        {paginationDots}

                        <button
                          className="button-yellow disabled:button-disabled"
                          onClick={() => setCurrentTaskStep(2)}
                          disabled={totalCounterCount === 0}
                        >
                          <span>Next</span>
                          <i>{Icons["ARROW_RIGHT"]}</i>
                        </button>
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-6 relative flex flex-col justify-center md:aspect-[10/7] bg-light-white p-8">
                      <MobileAccordion title="Instructions">
                        <p className="type-body-s">
                          Imagine you are doing this task while driving. Step through it as you normally would and log
                          each interaction — this simulates the steps and distraction that can occur while your vehicle
                          is on the move.
                        </p>
                        <p className="type-body-s">
                          Start counting from the first tap, press or voice command. Stop when your call is over.
                        </p>
                        <p className="type-body-s">
                          Count every action you make. This includes taps, button presses, voice commands, repeated
                          commands, corrections and confirmations. If you spell out ‘On my way’ on a keyboard, that’s
                          one tap per letter.
                        </p>
                      </MobileAccordion>

                      <div className="w-full md:mt-6">
                        <MobileAccordion title="Some examples">
                          <div className="wysiwyg flex flex-col gap-2">
                            <ul className="wp-block-list !mt-0">
                              <li className="!type-body-s">
                                <strong>Tap or touch</strong> — A screen tap, swipe or on-screen selection
                              </li>
                              <li className="!type-body-s">
                                <strong>A press or turn</strong> — Button, steering-wheel control, dial or knob
                              </li>
                              <li className="!type-body-s">
                                <strong>Voice command</strong> — A wake word like “Hello Siri”, spoken command, repeat
                                or correction
                              </li>
                            </ul>
                          </div>
                        </MobileAccordion>
                      </div>
                    </div>
                  </div>

                  <div className="md:hidden grid-auto !w-full">
                    <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
                      <button className="button-grey" onClick={goBack}>
                        <i>{Icons["ARROW_LEFT"]}</i>
                        <span>Back</span>
                      </button>

                      <button
                        className="button-yellow disabled:button-disabled"
                        onClick={() => setCurrentTaskStep(2)}
                        disabled={totalCounterCount === 0}
                      >
                        <span>Next</span>
                        <i>{Icons["ARROW_RIGHT"]}</i>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {currentTaskStep === 2 && (
                <>
                  <div className="grid-auto !w-full gap-6 md:gap-0 rounded-md overflow-hidden bg-white">
                    <div className="col-span-12 md:col-span-6 flex flex-col justify-between items-center gap-2 p-6">
                      <span className="type-label-caps text-mid-grey text-left w-full">
                        Task {currentTaskIndex + 1} of {activeTasks.length}
                      </span>

                      <div className="w-full flex flex-col gap-4">
                        <div className="flex flex-col gap-1">
                          <div className="type-title-m md:type-title-s mt-6 md:mt-0 text-center">
                            It took{" "}
                            <span
                              className={twMerge(
                                "w-[32px] h-[32px] inline-flex items-center justify-center rounded-full",
                                `bg-${distractionStarFill}`,
                                distractionTextFill,
                              )}
                            >
                              {totalCounterCount}
                            </span>{" "}
                            steps.
                            <br />
                            That’s a <span className="lowercase">{distractionLabel}</span> task.
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-3 md:gap-2 my-4">
                          <div className="order-2 md:order-1 flex md:flex-col gap-4 md:gap-1 items-center justify-center md:border-r md:border-solid md:border-light-grey mt-6 md:mt-0">
                            <div className="md:w-full md:h-[90px] flex items-center justify-center">
                              <svg
                                width="52"
                                height="52"
                                viewBox="0 0 52 52"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M36.0677 7.23423C36.8591 6.81106 37.8429 7.10935 38.2661 7.90073L38.2682 7.90285L38.2725 7.91131C38.2749 7.91588 38.2775 7.92163 38.2809 7.92824C38.2879 7.94163 38.2973 7.95932 38.3084 7.98113C38.331 8.02566 38.363 8.08948 38.4015 8.16945C38.4785 8.32926 38.5862 8.56056 38.7189 8.86769C38.9852 9.48368 39.354 10.4045 39.7832 11.6628C40.6415 14.1793 41.7432 18.0472 42.7645 23.535C43.7849 29.0186 44.3335 33.9512 44.6265 37.5125C44.773 39.2938 44.8551 40.7348 44.9015 41.7337C44.9247 42.2332 44.9398 42.623 44.9481 42.889C44.9522 43.0217 44.9549 43.1238 44.9565 43.1937C44.9574 43.2284 44.9583 43.2556 44.9587 43.2741V43.3037C44.9738 44.2007 44.2582 44.9427 43.3612 44.9583C42.4642 44.9735 41.7243 44.2579 41.7087 43.3609V43.3418C41.7083 43.3269 41.7073 43.3032 41.7065 43.272C41.705 43.2096 41.702 43.1153 41.6981 42.9906C41.6903 42.7404 41.6782 42.368 41.6558 41.8861C41.6109 40.9215 41.5301 39.5183 41.387 37.7791C41.1007 34.2991 40.5649 29.481 39.5695 24.1317C38.5749 18.787 37.511 15.0706 36.7067 12.7123C36.3047 11.5334 35.9668 10.6935 35.7355 10.1584C35.6199 9.89084 35.5307 9.69798 35.4731 9.57863C35.4446 9.51943 35.424 9.47793 35.4118 9.45379C35.4056 9.44167 35.3991 9.43051 35.3991 9.43051L35.4012 9.43263C34.978 8.64124 35.2763 7.65744 36.0677 7.23423Z"
                                  fill="black"
                                />
                                <path
                                  d="M7.04199 43.3333V43.2212C7.04256 43.1506 7.04403 43.046 7.04622 42.9123C7.05061 42.6451 7.05861 42.255 7.07585 41.7549C7.1104 40.7527 7.1797 39.3066 7.31706 37.521C7.5916 33.952 8.14131 29.0093 9.2404 23.5138C10.3359 18.0364 11.4351 14.1792 12.2703 11.6734C12.6879 10.4208 13.0407 9.50629 13.2923 8.8952C13.4181 8.58965 13.519 8.35885 13.5907 8.20118C13.6262 8.12298 13.6546 8.06252 13.6753 8.01922C13.6856 7.9975 13.6943 7.97939 13.7007 7.96632L13.7134 7.94093L13.7959 7.79494C14.2367 7.09978 15.1405 6.83748 15.8927 7.21307C16.6952 7.61428 17.0213 8.58987 16.6206 9.39243C16.6187 9.39643 16.6139 9.40517 16.6079 9.41782C16.5958 9.44323 16.5761 9.48657 16.5487 9.54689C16.4934 9.66845 16.4078 9.86158 16.2969 10.1309C16.0745 10.6708 15.7501 11.5172 15.3553 12.7017C14.5656 15.0707 13.498 18.7974 12.4269 24.1528C11.3595 29.4903 10.8257 34.2982 10.5586 37.7707C10.4252 39.5052 10.359 40.9036 10.3258 41.8649C10.3093 42.3455 10.3003 42.7185 10.2962 42.9673C10.2942 43.0913 10.2925 43.1851 10.292 43.2466V43.3333C10.292 44.2308 9.56445 44.9583 8.66699 44.9583C7.76953 44.9583 7.04199 44.2308 7.04199 43.3333Z"
                                  fill="black"
                                />
                                <path
                                  d="M24.3753 10.8333V8.66668C24.3753 7.76922 25.1029 7.04168 26.0003 7.04168C26.8978 7.04168 27.6253 7.76922 27.6253 8.66668V10.8333C27.6253 11.7308 26.8978 12.4583 26.0003 12.4583C25.1029 12.4583 24.3753 11.7308 24.3753 10.8333Z"
                                  fill="black"
                                />
                                <path
                                  d="M24.3753 24.9167V18.4167C24.3753 17.5192 25.1029 16.7917 26.0003 16.7917C26.8978 16.7917 27.6253 17.5192 27.6253 18.4167V24.9167C27.6253 25.8141 26.8978 26.5417 26.0003 26.5417C25.1029 26.5417 24.3753 25.8141 24.3753 24.9167Z"
                                  fill="black"
                                />
                                <path
                                  d="M24.3753 43.3333V33.5833C24.3753 32.6859 25.1029 31.9583 26.0003 31.9583C26.8978 31.9583 27.6253 32.6859 27.6253 33.5833V43.3333C27.6253 44.2308 26.8978 44.9583 26.0003 44.9583C25.1029 44.9583 24.3753 44.2308 24.3753 43.3333Z"
                                  fill="black"
                                />
                              </svg>
                            </div>

                            <div className="flex flex-col gap-1 items-center justify-center">
                              <span className="type-title-xl md:type-title-s">{drivingDistanceMeters.toFixed(1)}m</span>
                              <span className="type-label-caps text-dark-grey">Driving 50km/h</span>
                            </div>
                          </div>

                          <div className="order-1 md:order-2 flex flex-col gap-1 items-center md:justify-between md:border-r md:border-solid md:border-light-grey">
                            <div className="w-full md:h-[90px] flex flex-col gap-4 items-center justify-center">
                              <span className="type-label-caps text-dark-grey bg-light-grey px-2 py-1 rounded-full">
                                Eyes-off road*
                              </span>
                              <span className="type-title-xl md:type-title-l">{eyesOffRoadSeconds.toFixed(2)}s</span>
                            </div>

                            <span className="flex items-center gap-2 md:flex-col justify-center">
                              <svg
                                width="20"
                                height="12"
                                viewBox="0 0 20 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle cx="6" cy="6" r="6" fill="#CE9FEA" />
                                <circle cx="13.1172" cy="6" r="6" fill="#C7EDF5" />
                              </svg>

                              <span className="type-label-caps text-dark-grey">touch &amp; press actions</span>
                            </span>
                          </div>

                          <div className="order-3 hidden md:flex flex-col gap-1 items-center">
                            <div className="w-full h-[90px] flex items-center justify-center gap-1">
                              {Array.from({ length: 5 }, (_, i) => (
                                <svg
                                  key={i}
                                  width="13"
                                  height="12"
                                  viewBox="0 0 13 12"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M5.99485 0.185565C6.02242 0.129851 6.06502 0.082953 6.11784 0.050164C6.17065 0.0173749 6.23158 0 6.29375 0C6.35591 0 6.41684 0.0173749 6.46966 0.050164C6.52247 0.082953 6.56507 0.129851 6.59265 0.185565L8.04624 3.12988C8.142 3.32367 8.28335 3.49133 8.45817 3.61847C8.63299 3.74561 8.83604 3.82843 9.04991 3.85982L12.3007 4.33554C12.3623 4.34447 12.4201 4.37045 12.4677 4.41055C12.5153 4.45065 12.5508 4.50327 12.57 4.56246C12.5893 4.62165 12.5916 4.68504 12.5767 4.74547C12.5618 4.8059 12.5302 4.86095 12.4857 4.9044L10.1348 7.19365C9.97972 7.34474 9.86372 7.53124 9.79674 7.7371C9.72977 7.94296 9.71382 8.16201 9.75028 8.3754L10.3053 11.6098C10.3162 11.6714 10.3095 11.7348 10.2861 11.7927C10.2627 11.8507 10.2234 11.9009 10.1729 11.9376C10.1223 11.9744 10.0624 11.9962 10 12.0005C9.93764 12.0049 9.87531 11.9916 9.82013 11.9622L6.9142 10.4343C6.72272 10.3338 6.5097 10.2813 6.29343 10.2813C6.07717 10.2813 5.86414 10.3338 5.67267 10.4343L2.76737 11.9622C2.7122 11.9914 2.64994 12.0045 2.58768 12.0001C2.52542 11.9957 2.46565 11.9739 2.41516 11.9372C2.36468 11.9004 2.32552 11.8503 2.30213 11.7924C2.27873 11.7346 2.27205 11.6713 2.28283 11.6098L2.83721 8.37603C2.87383 8.16254 2.85797 7.94335 2.79099 7.73736C2.724 7.53137 2.60792 7.34477 2.45273 7.19365L0.101814 4.90503C0.0568812 4.86163 0.0250401 4.80648 0.0099185 4.74587C-0.00520313 4.68526 -0.00299741 4.62162 0.0162843 4.5622C0.0355661 4.50278 0.0711485 4.44997 0.118979 4.40979C0.166809 4.3696 0.224963 4.34366 0.286817 4.33492L3.53695 3.85982C3.75106 3.82867 3.9544 3.74596 4.12946 3.61881C4.30451 3.49165 4.44605 3.32386 4.54188 3.12988L5.99485 0.185565Z"
                                    className={i < distractionStars ? `fill-${distractionStarFill}` : "fill-[#F3F3F3]"}
                                  />
                                </svg>
                              ))}
                            </div>

                            <span className="type-title-s">{totalCounterCount} steps</span>
                            <span className="type-label-caps text-dark-grey">{distractionLabel}</span>
                          </div>
                        </div>

                        <div className="w-full flex items-center justify-center gap-[2px] my-4">
                          {counterLog.map((type, i) => (
                            <span
                              key={i}
                              className={twMerge(
                                "flex flex-grow h-[10px]",
                                COUNTER_COLORS[type],
                                i === 0 && "rounded-l-full",
                                i === counterLog.length - 1 && "rounded-r-full",
                              )}
                            />
                          ))}
                        </div>
                        <div className="w-full flex items-center justify-center gap-6">
                          <div className="flex items-center gap-2">
                            <i className="w-[8px] h-[8px] flex rounded-full bg-[#A2E0EE99]" />
                            <span className="type-label-caps text-dark-grey">Touch</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <i className="w-[8px] h-[8px] flex rounded-full bg-[#AD5FDC99]" />
                            <span className="type-label-caps text-dark-grey">Press</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <i className="w-[8px] h-[8px] flex rounded-full bg-[#607CDE99]" />
                            <span className="type-label-caps text-dark-grey">Voice</span>
                          </div>
                        </div>
                      </div>

                      <div className="w-full hidden md:flex items-center justify-between">
                        <button className="button-grey" onClick={() => setCurrentTaskStep(1)}>
                          <i>{Icons["ARROW_LEFT"]}</i>
                          <span>Back</span>
                        </button>

                        {paginationDots}

                        <button
                          className="button-yellow"
                          onClick={() => {
                            goNext()
                            setCurrentTaskStep(1)
                          }}
                        >
                          <span>Next</span>
                          <i>{Icons["ARROW_RIGHT"]}</i>
                        </button>
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-6 relative flex flex-col justify-center md:aspect-[10/7] bg-light-white p-8">
                      <div className="flex flex-col gap-4">
                        <span className="type-label-caps text-dark-grey">Did anything make the task harder?</span>

                        <ul className="flex flex-col gap-4 my-4">
                          {TASK_QUESTIONS.map((q, i) => {
                            const checked = selectedQuestions.includes(q.issue)
                            return (
                              <li key={i} className="flex gap-3 items-center">
                                <input
                                  type="checkbox"
                                  id={kebabCase(q.issue)}
                                  checked={checked}
                                  onChange={() => toggleQuestion(q.issue)}
                                  className="sr-only"
                                />
                                <label
                                  htmlFor={kebabCase(q.issue)}
                                  className={twMerge(
                                    "w-6 h-6 md:w-4 md:h-4 flex-shrink-0 border border-dark-grey rounded-[2px] cursor-pointer aaa-animate [&_svg]:w-full [&_svg]:h-full [&_svg]:scale-150",
                                    checked && "bg-yellow border-yellow",
                                  )}
                                >
                                  {checked && Icons["TICK"]}
                                </label>
                                <label
                                  htmlFor={kebabCase(q.issue)}
                                  className="type-label-caps text-dark-grey cursor-pointer"
                                >
                                  {q.issue}
                                </label>
                              </li>
                            )
                          })}
                        </ul>
                        <button className="button-grey w-auto self-start" onClick={toggleNote}>
                          <i>{Icons["RESEARCH"]}</i>
                          <span>Add a note</span>
                        </button>
                        <textarea
                          value={noteText}
                          onChange={(e) => updateNoteText(e.target.value)}
                          placeholder="Add any additional notes..."
                          className={twMerge(
                            "w-full p-4 type-body-s border border-solid border-mid-grey rounded-md",
                            !isNoteVisible && "hidden",
                          )}
                        />
                      </div>

                      <p className="type-body-xs mt-8">
                        *These time estimates are indicative only, based on step counts rather than a direct measurement
                        of your eyes, attention or driving performance.
                      </p>
                    </div>
                  </div>

                  <div className="md:hidden grid-auto !w-full">
                    <div className="col-span-12 bg-white p-4 rounded-md flex justify-between">
                      <button className="button-grey" onClick={() => setCurrentTaskStep(1)}>
                        <i>{Icons["ARROW_LEFT"]}</i>
                        <span>Back</span>
                      </button>

                      <button
                        className="button-yellow disabled:button-disabled"
                        onClick={() => {
                          goNext()
                          setCurrentTaskStep(1)
                        }}
                      >
                        <span>{page === PAGE_SUMMARY - 1 ? "See summary" : "Next"}</span>
                        <i>{Icons["ARROW_RIGHT"]}</i>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {page === PAGE_SUMMARY && (
            <div ref={summaryRef} className="grid-auto !w-full gap-6 rounded-md overflow-hidden bg-white p-4 md:p-6">
              <div className="hidden md:flex col-span-12">
                <span className="type-label-caps text-mid-grey">Results</span>
              </div>

              <div className="col-span-12 lg:col-span-10 lg:col-start-2 xl:col-span-8 xl:col-start-3 flex flex-col justify-between items-center gap-8">
                <div className="w-full flex flex-col gap-6 items-center">
                  <span className="bg-light-grey type-label-caps px-2 py-1 rounded-full">Your results</span>
                  <h2 className="type-title-xl !text-center">
                    You are averaging{" "}
                    <span
                      className={twMerge(
                        "w-[48px] h-[48px] inline-flex items-center justify-center rounded-full",
                        `bg-${averageStarFill}`,
                        averageTextFill,
                      )}
                    >
                      {averageSteps}
                    </span>{" "}
                    steps per task. Let's <span className="underline">reduce it</span>.
                  </h2>
                </div>
              </div>

              <div className="col-span-12 lg:col-span-8 lg:col-start-2 xl:col-span-6 xl:col-start-3 border border-solid border-grey rounded-md overflow-hidden [&_canvas]:scale-105 [&_canvas]:origin-bottom relative">
                <div className="absolute top-4 left-4 flex flex-col gap-1 z-10">
                  <span className="type-body-s">Average eyes-off-road*</span>
                  <span className="type-title-l">
                    {(activeTasks.length ? totalEyesOffRoadSeconds / activeTasks.length : 0).toFixed(2)}s
                  </span>
                  <span className="type-title-s">
                    {Math.round(activeTasks.length ? totalDrivingDistanceMeters / activeTasks.length : 0)}m
                  </span>

                  <div className="flex items-center justify-start gap-1">
                    <span className="type-title-s h-[1.2em] flex items-center justify-center">
                      <svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="6" cy="6" r="6" fill="#CE9FEA" />
                        <circle cx="13.1172" cy="6" r="6" fill="#C7EDF5" />
                      </svg>
                    </span>
                    <span className="type-label-caps text-dark-grey">touch &amp; press actions</span>
                  </div>
                </div>
                <DistractionChart
                  interactive={false}
                  highlightStep={averageSteps}
                  highlightLabel="Your Average"
                  showCard={false}
                  showYAxis={false}
                  showTitle={false}
                />
              </div>
              <div className="col-span-12 lg:col-span-2 border border-solid border-grey rounded-md">
                <div className="flex flex-col justify-between p-4 gap-6">
                  <span className="type-body-s">Interaction modes</span>

                  <div className="w-full flex md:flex-col gap-4 justify-between">
                    {COUNTER_TYPES.map((type, i) => (
                      <div key={i} className="flex gap-3 items-center">
                        <span
                          className={twMerge(
                            "w-[32px] h-[32px] md:w-[46px] md:h-[46px] rounded-full overflow-hidden flex items-center justify-center [&_svg]:w-3/5 [&_svg]:h-auto",
                            COUNTER_COLORS[type.key],
                          )}
                        >
                          {type.icon}
                        </span>
                        <div className="flex flex-col gap-1">
                          <span className="type-label-caps text-dark-grey">{type.key}</span>
                          <span className="type-title-s md:type-title-m">
                            {Math.round(getAverageCounterPercentage(type.key))}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-span-12 lg:col-span-8 lg:col-start-3 xl:col-span-6 xl:col-start-4 flex flex-col justify-between items-center gap-6">
                <div className="w-full flex flex-col gap-6 bg-yellow rounded-md p-6">
                  <div className="w-full flex flex-col gap-2">
                    <h4 className="!type-title-s">Quick Wins for Safer, Smarter Driving Habits</h4>
                    <p className="type-body-s">
                      Small changes to how you use your car's screen and voice controls can make a big difference to how
                      much attention they pull away from the road.
                    </p>
                  </div>

                  <div className="w-full flex flex-col gap-2">
                    {activeTasks.map((task, i) => {
                      const log = taskCounters[i] ?? []
                      const steps = log.length
                      const { stars } = getDistractionForSteps(steps)
                      const isTopRankedTask = maxTaskSteps > 0 && steps === maxTaskSteps
                      const showTopRankedMessage = isTopRankedTask && stars < 5

                      const icon = taskMode === "custom" ? Icons["RESEARCH"] : commonTaskSet[i]?.icon
                      return (
                        <Fragment key={i}>
                          {showTopRankedMessage && (
                            <div className="flex gap-4 items-start p-4 bg-light-white rounded-md">
                              <span className="flex items-center justify-center type-label-l w-[32px] min-w-[32px] h-[32px] bg-yellow rounded-full">
                                {icon}
                              </span>
                              <p className="type-body-s">
                                <strong>{task}</strong> took the most steps to complete. If possible, try setting this
                                task up before you start driving or while stationary to reduce distraction.
                              </p>
                            </div>
                          )}
                        </Fragment>
                      )
                    })}

                    {activeTasks.map((task, i) => {
                      const log = taskCounters[i] ?? []
                      const steps = log.length
                      const { stars } = getDistractionForSteps(steps)
                      const isTopRankedTask = maxTaskSteps > 0 && steps === maxTaskSteps
                      const showTopRankedMessage = isTopRankedTask && stars < 5
                      const showElevatedStepsMessage = !showTopRankedMessage && stars <= 3

                      const icon = taskMode === "custom" ? Icons["RESEARCH"] : commonTaskSet[i]?.icon
                      return (
                        <Fragment key={i}>
                          {showElevatedStepsMessage && (
                            <div className="flex gap-4 items-start p-4 bg-light-white rounded-md">
                              <span className="flex items-center justify-center type-label-l w-[32px] min-w-[32px] h-[32px] bg-yellow rounded-full">
                                {icon}
                              </span>
                              <p className="type-body-s">
                                <strong>{task}</strong> took several steps and added interaction time. Consider
                                completing it before driving, using a saved shortcut, or choosing a simpler mode where
                                available.
                              </p>
                            </div>
                          )}
                        </Fragment>
                      )
                    })}
                  </div>
                </div>
                <div className="w-full flex flex-col gap-4">
                  {activeTasks.map((task, i) => {
                    const log = taskCounters[i] ?? []
                    const steps = log.length
                    const { label, stars, starFill, textFill, eyesOffRoadSeconds, drivingDistanceMeters } =
                      getDistractionForSteps(steps)
                    const answers = taskQuestionAnswers[i] ?? []
                    const note = taskNotes[i]
                    const icon = taskMode === "custom" ? Icons["RESEARCH"] : commonTaskSet[i]?.icon

                    const taskInfo = taskMode === "custom" ? null : commonTaskSet[i]
                    const showTaskTip = stars < 5 && !!(taskInfo?.recommendation || taskInfo?.tip)
                    const flaggedIssues = answers
                      .filter((issue) => firstFlaggedIssueTaskIndex[issue] === i)
                      .map((issue) => TASK_QUESTIONS.find((q) => q.issue === issue))
                      .filter((q): q is (typeof TASK_QUESTIONS)[number] => q !== undefined)
                    const isHighlyDistracting = steps >= 7 && steps <= 8
                    const isUnacceptable = steps >= 9
                    const hasInsights = showTaskTip || flaggedIssues.length > 0 || isHighlyDistracting || isUnacceptable

                    return (
                      <div key={i} className="flex flex-col gap-3 border border-solid border-grey rounded-md p-5">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <span className="type-title-s">{task}</span>
                          </div>

                          <div className="flex gap-3 items-center">
                            <span className="type-label-l order-2 md:order-1">{label} task</span>
                            <span
                              className={twMerge(
                                "w-[32px] h-[32px] inline-flex items-center justify-center rounded-full type-label-l order-1 md:order-2",
                                `bg-${starFill}`,
                                textFill,
                              )}
                            >
                              {steps}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 md:grid-cols-5 gap-5 my-6 md:mb-0">
                          <div className="col-span-1 flex flex-col gap-4 border-r border-solid border-light-grey">
                            <span className="type-label-caps text-mid-grey">Eyes-off road*</span>
                            <span className="type-title-l">{eyesOffRoadSeconds.toFixed(2)}s</span>
                          </div>
                          <div className="col-span-1 flex flex-col gap-4 border-r border-solid border-light-grey">
                            <span className="type-label-caps text-mid-grey">Distance travelled</span>
                            <span className="type-title-l">{drivingDistanceMeters.toFixed(1)}m</span>
                          </div>
                          <div className="col-span-1 flex flex-col justify-center gap-4">
                            <div className="w-full flex gap-1 items-center justify-start">
                              {Array.from({ length: 5 }, (_, i) => (
                                <svg
                                  key={i}
                                  width="13"
                                  height="12"
                                  viewBox="0 0 13 12"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M5.99485 0.185565C6.02242 0.129851 6.06502 0.082953 6.11784 0.050164C6.17065 0.0173749 6.23158 0 6.29375 0C6.35591 0 6.41684 0.0173749 6.46966 0.050164C6.52247 0.082953 6.56507 0.129851 6.59265 0.185565L8.04624 3.12988C8.142 3.32367 8.28335 3.49133 8.45817 3.61847C8.63299 3.74561 8.83604 3.82843 9.04991 3.85982L12.3007 4.33554C12.3623 4.34447 12.4201 4.37045 12.4677 4.41055C12.5153 4.45065 12.5508 4.50327 12.57 4.56246C12.5893 4.62165 12.5916 4.68504 12.5767 4.74547C12.5618 4.8059 12.5302 4.86095 12.4857 4.9044L10.1348 7.19365C9.97972 7.34474 9.86372 7.53124 9.79674 7.7371C9.72977 7.94296 9.71382 8.16201 9.75028 8.3754L10.3053 11.6098C10.3162 11.6714 10.3095 11.7348 10.2861 11.7927C10.2627 11.8507 10.2234 11.9009 10.1729 11.9376C10.1223 11.9744 10.0624 11.9962 10 12.0005C9.93764 12.0049 9.87531 11.9916 9.82013 11.9622L6.9142 10.4343C6.72272 10.3338 6.5097 10.2813 6.29343 10.2813C6.07717 10.2813 5.86414 10.3338 5.67267 10.4343L2.76737 11.9622C2.7122 11.9914 2.64994 12.0045 2.58768 12.0001C2.52542 11.9957 2.46565 11.9739 2.41516 11.9372C2.36468 11.9004 2.32552 11.8503 2.30213 11.7924C2.27873 11.7346 2.27205 11.6713 2.28283 11.6098L2.83721 8.37603C2.87383 8.16254 2.85797 7.94335 2.79099 7.73736C2.724 7.53137 2.60792 7.34477 2.45273 7.19365L0.101814 4.90503C0.0568812 4.86163 0.0250401 4.80648 0.0099185 4.74587C-0.00520313 4.68526 -0.00299741 4.62162 0.0162843 4.5622C0.0355661 4.50278 0.0711485 4.44997 0.118979 4.40979C0.166809 4.3696 0.224963 4.34366 0.286817 4.33492L3.53695 3.85982C3.75106 3.82867 3.9544 3.74596 4.12946 3.61881C4.30451 3.49165 4.44605 3.32386 4.54188 3.12988L5.99485 0.185565Z"
                                    className={i < stars ? `fill-${starFill}` : "fill-[#F3F3F3]"}
                                  />
                                </svg>
                              ))}
                            </div>
                            <span className="type-label-caps text-mid-grey">{label}</span>
                          </div>
                        </div>

                        <div className="w-full flex items-center justify-start md:justify-end gap-6">
                          <div className="flex items-center gap-2">
                            <i className="w-[8px] h-[8px] flex rounded-full bg-[#A2E0EE99]" />
                            <span className="type-label-caps text-dark-grey">Touch</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <i className="w-[8px] h-[8px] flex rounded-full bg-[#AD5FDC99]" />
                            <span className="type-label-caps text-dark-grey">Press</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <i className="w-[8px] h-[8px] flex rounded-full bg-[#607CDE99]" />
                            <span className="type-label-caps text-dark-grey">Voice</span>
                          </div>
                        </div>
                        {log.length > 0 && (
                          <div className="w-full flex items-center gap-[2px]">
                            {log.map((type, j) => (
                              <span
                                key={j}
                                className={twMerge(
                                  "flex flex-grow h-[8px]",
                                  COUNTER_COLORS[type],
                                  j === 0 && "rounded-l-full",
                                  j === log.length - 1 && "rounded-r-full",
                                )}
                              />
                            ))}
                          </div>
                        )}

                        {hasInsights && (
                          <div className="w-full flex flex-col gap-3 pt-4 mt-2 border-t border-solid border-light-grey">
                            {isUnacceptable ? (
                              <div
                                className={twMerge("flex gap-3 items-start p-3 rounded-sm", `bg-${starFill}`, textFill)}
                              >
                                <span className="type-body-s">
                                  Any task that takes 9 or above steps is considered unacceptable for use while driving.
                                  Avoid this task while driving.
                                </span>
                              </div>
                            ) : (
                              isHighlyDistracting && (
                                <div
                                  className={twMerge(
                                    "flex gap-3 items-start p-3 rounded-sm",
                                    `bg-${starFill}`,
                                    textFill,
                                  )}
                                >
                                  <span className="type-body-s">
                                    Any task that takes 7 - 8 steps is considered highly distracting.
                                  </span>
                                </div>
                              )
                            )}

                            {showTaskTip && (
                              <div className="flex gap-4 items-start p-4 bg-light-white rounded-md">
                                <span className="flex items-center justify-center type-label-l w-[32px] min-w-[32px] h-[32px] bg-yellow rounded-full">
                                  {icon}
                                </span>
                                <div className="flex flex-col gap-1">
                                  {taskInfo?.recommendation && <p className="type-body-s">{taskInfo.recommendation}</p>}
                                  {taskInfo?.tip && (
                                    <p className="type-body-s">
                                      <strong>Tip: </strong>
                                      {taskInfo.tip}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}

                            {flaggedIssues.map((q) => (
                              <div key={q.issue} className="flex gap-4 items-start p-4 bg-light-white rounded-md">
                                <span className="flex items-center justify-center type-label-l w-[32px] min-w-[32px] h-[32px] bg-yellow rounded-full">
                                  {icon}
                                </span>
                                <div className="flex flex-col gap-1 !type-body-s">
                                  <strong>{q.issue}</strong>
                                  {q.recommendation && <p className="type-body-s">{q.recommendation}</p>}
                                  {q.tip && (
                                    <p className="type-body-s">
                                      <strong>Tip: </strong>
                                      {q.tip}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>

                <div
                  data-pdf-hide="true"
                  className="w-full flex flex-col md:flex-row gap-4 items-center justify-center max-w-[750px] px-4"
                >
                  <button className="button-grey w-full md:w-auto" onClick={exportSummary} disabled={isExporting}>
                    <span>{isExporting ? "Preparing PDF..." : "Download"}</span>
                    <i>{Icons["DOWNLOAD"]}</i>
                  </button>

                  <button className="button-grey w-full md:w-auto" onClick={copyShareLink}>
                    <span>{isLinkCopied ? "Link copied" : "Share"}</span>
                    <i>{Icons["LINK"]}</i>
                  </button>
                </div>

                <p className="type-body-s max-w-[500px] mx-auto text-center">
                  *These time estimates are indicative only, based on step counts rather than a direct measurement of
                  your eyes, attention or driving performance.
                </p>

                <div data-pdf-hide="true" className="w-full flex items-center justify-center">
                  <button className="button-black" onClick={restart}>
                    <span>Test another car</span>
                    <i>{Icons["ADD"]}</i>
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="grid-auto !w-full gap-6 mt-6 md:mt-0">
        <div className="col-span-12 md:col-span-4 flex bg-white rounded-md overflow-hidden hover:bg-yellow aaa-animate">
          {props.document && (
            <Link
              href={props.document.uri}
              target="_blank"
              className="w-full flex rounded-md gap-4 p-4 relative overflow-hidden aaa-animate"
            >
              <div className="flex flex-grow flex-col gap-4 justify-between">
                <div className="flex flex-grow flex-col gap-2">
                  <span className="type-label-caps text-mid-grey">Print Version</span>
                  <h4 className="!type-title-xs">{props.document.title}</h4>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {props?.document?.documentTopics?.nodes
                    ?.concat(props?.document?.documentTypes?.nodes ?? [])
                    .map((category, i) => (
                      <Chip key={i} color="outline">
                        {category.name}
                      </Chip>
                    ))}
                </div>
              </div>

              {props.document?.featuredImage?.node ? (
                <ResponsiveImage
                  {...props.document.featuredImage.node}
                  aspect={0.71}
                  className="rounded-sm w-1/4 overflow-hidden"
                />
              ) : (
                <Logo />
              )}
            </Link>
          )}
        </div>
        <div className="col-span-12 md:col-span-4 flex bg-white rounded-md p-4 flex-col gap-4">
          <EditableText as="span" store="title_2" className="type-label-caps text-mid-grey" placeholder="Text..." />
          <EditableText as="span" store="text_2" className="type-body-s" placeholder="Text..." />
        </div>
        <div className="col-span-12 md:col-span-4 flex bg-white rounded-md p-4 flex-col gap-4">
          <EditableText as="span" store="title_3" className="type-label-caps text-mid-grey" placeholder="Text..." />
          <div className="grid grid-cols-3 w-full gap-4">
            {props.researchPartners?.map((node, i) => (
              <ResponsiveImage
                {...node?.logo}
                key={i}
                objectFit="contain"
                className="flex w-full items-center justify-center"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
})

// 1 inch — the standard Word document default page margin.
const PDF_MARGIN_MM = 25.4

const PAGE_HOME = 0
const WELCOME = 1
const SETUP_1 = 2
const SETUP_2 = 3
const SETUP_3 = 4
const INSTRUCTIONS = 5
const QUESTIONS_START = 6

const COUNTER_TYPES = [
  {
    key: "press",
    label: "A press or turn",
    icon: (
      <svg width="49" height="48" viewBox="0 0 49 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M40.9911 28.6077V19.4548C40.9911 18.0486 42.1309 16.9091 43.5372 16.9091H47.8448V31.1537H43.5372C42.1309 31.1537 40.9911 30.014 40.9911 28.6077Z"
          fill="black"
        />
        <path
          d="M39.2543 11.2986C39.8366 11.6786 39.9994 12.4631 39.6145 13.0453C39.3728 13.4104 38.9682 13.6127 38.5537 13.6127C38.3169 13.6127 38.08 13.5437 37.8629 13.4055L32.5241 9.89241C31.9418 9.51245 31.779 8.72796 32.1639 8.14572C32.5487 7.56348 33.3332 7.40558 33.9155 7.78551L39.2543 11.2986Z"
          fill="black"
        />
        <path
          d="M39.2544 34.3419L33.9156 37.855C33.6985 37.9931 33.4616 38.0622 33.2199 38.0622C32.8103 38.0622 32.4057 37.8649 32.1639 37.4948C31.7791 36.9126 31.9419 36.133 32.5241 35.7481L37.863 32.235C38.4501 31.8501 39.2297 32.013 39.6146 32.5952C39.9995 33.1774 39.8367 33.957 39.2544 34.3419Z"
          fill="black"
        />
        <path
          d="M0.377578 25.1363L0.377578 35.3498C0.377578 36.7699 1.54265 37.9222 2.97869 37.9222L5.01642 37.9222C5.85789 37.9222 6.67296 38.2731 7.25072 38.88C8.17129 39.8639 9.47783 40.421 10.8275 40.421L19.0145 40.421C20.3091 40.421 21.4885 39.6695 22.028 38.5102L26.4462 28.9583C26.7459 28.3039 27.4147 27.8772 28.1459 27.8772L35.0815 27.8772C37.057 27.8772 38.6655 26.2864 38.6655 24.3257C38.6655 22.365 37.0569 20.7813 35.0815 20.7813L21.9535 20.7813L22.8813 19.8591C23.9193 18.8325 23.9193 17.1634 22.8813 16.1392C21.1264 14.3967 18.2544 14.2758 16.3507 15.8666L9.76554 21.3716C8.85215 22.1374 7.67989 22.5618 6.48117 22.5618L2.9763 22.5618C1.54032 22.5618 0.375187 23.714 0.375186 25.1341L0.377578 25.1363ZM17.5426 17.2584C18.7221 16.2745 20.4937 16.348 21.5797 17.4291C21.7403 17.5879 21.8194 17.7942 21.8194 18.0004C21.8194 18.2067 21.7403 18.4201 21.5797 18.5765L17.5043 22.6069L35.0817 22.6069C36.0454 22.6069 36.8246 23.3774 36.8246 24.3305C36.8246 25.2836 36.0454 26.0612 35.0817 26.0612L28.1461 26.0612C26.6981 26.0612 25.3724 26.8981 24.7707 28.2044L20.3524 37.7563C20.1127 38.2732 19.5924 38.6051 19.0147 38.6051L10.8277 38.6051C9.98145 38.6051 9.17116 38.2542 8.5934 37.6473C7.67283 36.6705 6.36629 36.1062 5.0166 36.1062L2.97888 36.1062C2.56174 36.1062 2.21891 35.7719 2.21891 35.3546L2.21891 25.1412C2.21891 24.7215 2.56173 24.3896 2.97888 24.3896L6.48375 24.3896C8.11636 24.3896 9.70577 23.8135 10.9572 22.768L17.5424 17.2629L17.5426 17.2584Z"
          fill="black"
          stroke="black"
          strokeWidth="0.75"
        />
      </svg>
    ),
  },
  {
    key: "touch",
    label: "Touch or tap",
    icon: (
      <svg width="48" height="50" viewBox="0 0 48 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M23.4866 48.9246H33.4382C34.8218 48.9246 35.9445 47.7724 35.9445 46.3522V44.337C35.9445 43.5049 36.2864 42.6988 36.8778 42.1274C37.8365 41.217 38.3793 39.925 38.3793 38.5902V30.4938C38.3793 29.2135 37.647 28.0471 36.5175 27.5136L27.2105 23.1442C26.573 22.8479 26.1572 22.1864 26.1572 21.4633V14.6044C26.1572 12.6508 24.6072 11.0601 22.6967 11.0601C20.7863 11.0601 19.2433 12.6509 19.2433 14.6044V27.5872L18.3447 26.6697C17.3445 25.6432 15.7182 25.6432 14.7203 26.6697C13.0224 28.4052 12.9046 31.2454 14.4546 33.1281L19.8185 39.6404C20.5646 40.5437 20.9781 41.703 20.9781 42.8885V46.3546C20.9781 47.7747 22.1008 48.9269 23.4845 48.9269L23.4866 48.9246ZM15.8107 31.9494C14.852 30.7829 14.9236 29.0309 15.977 27.9569C16.1318 27.7981 16.3327 27.7198 16.5337 27.7198C16.7347 27.7198 16.9426 27.7981 17.0951 27.9569L21.0221 31.9873V14.6042C21.0221 13.6512 21.7728 12.8807 22.7015 12.8807C23.6301 12.8807 24.3878 13.6512 24.3878 14.6042V21.4631C24.3878 22.8951 25.2032 24.2062 26.476 24.8012L35.783 29.1707C36.2866 29.4077 36.6099 29.9222 36.6099 30.4936V38.59C36.6099 39.4269 36.2681 40.2282 35.6767 40.7996C34.725 41.71 34.1752 43.0021 34.1752 44.3368V46.352C34.1752 46.7646 33.8495 47.1036 33.4429 47.1036H23.4914C23.0825 47.1036 22.7591 46.7646 22.7591 46.352V42.8859C22.7591 41.2714 22.1977 39.6995 21.179 38.462L15.8151 31.9496L15.8107 31.9494Z"
          fill="black"
          stroke="black"
          strokeWidth="0.75"
        />
        <path
          d="M28.8279 20.8834C32.2052 17.4173 32.2052 11.7722 28.8279 8.30845C25.4507 4.84233 19.9503 4.84233 16.5754 8.30845C13.1982 11.7746 13.1982 17.4197 16.5754 20.8834L17.8298 19.5961C15.1455 16.8411 15.1455 12.3554 17.8298 9.59386C20.514 6.83895 24.8913 6.83895 27.5755 9.59386C30.2598 12.3488 30.2598 16.8345 27.5755 19.5961L28.8298 20.8834H28.8279Z"
          fill="black"
          stroke="black"
          strokeWidth="0.75"
        />
        <path
          d="M36.5761 14.5975C36.5761 10.7923 35.1323 7.21719 32.5127 4.53577C27.1073 -1.01192 18.3081 -1.01192 12.8959 4.53577C7.49043 10.0835 7.49043 19.1142 12.8959 24.6688L14.1502 23.3815C9.43775 18.5379 9.43775 10.6669 14.1502 5.82301C18.8696 0.986511 26.5388 0.986511 31.2585 5.82301C33.5408 8.16539 34.7998 11.2854 34.7998 14.5998C34.7998 17.9142 33.5408 21.0345 31.2585 23.3766L32.5128 24.664C35.1324 21.9755 36.5762 18.4005 36.5762 14.6023L36.5761 14.5975Z"
          fill="black"
          stroke="black"
          strokeWidth="0.75"
        />
      </svg>
    ),
  },
  {
    key: "voice",
    label: "A voice command",
    icon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M16.1787 3.79133C22.242 3.79656 26.7668 6.72223 29.433 10.5039C31.9767 14.1126 32.9397 18.4646 32.1598 22.0754L35.3524 28.0838C35.7606 28.8505 35.8548 29.8057 35.4728 30.5227C35.0907 31.2397 34.4731 31.5616 33.9602 31.7553C32.9371 32.1452 31.9505 32.1138 31.9505 32.1138H31.9426C31.4349 32.1138 31.5998 32.1399 31.5291 32.3048C31.4794 32.4199 31.4663 32.5429 31.4611 32.6214L31.9191 34.2413L31.9217 34.2387C32.0813 34.8092 31.8327 35.4163 31.3172 35.7094L29.747 36.5991L30.4876 37.2141H30.485C30.7624 37.4443 30.9299 37.7819 30.9482 38.143C30.9639 38.5042 30.8278 38.8574 30.5714 39.1113C29.8752 39.8074 29.5324 40.7625 29.03 41.8066C28.7788 42.33 28.4805 42.89 27.9414 43.3715C27.4049 43.853 26.6068 44.1644 25.772 44.1644H17.3484C16.0871 44.1644 14.9985 43.6437 14.2553 42.9214C13.5121 42.2017 13.0698 41.3251 12.7715 40.5034C12.1775 38.86 12.1461 37.2898 12.1461 37.2898V37.2924C12.1382 36.9523 12.2639 36.6199 12.4994 36.3739C12.7323 36.1253 13.0568 35.9814 13.3969 35.9709C14.1087 35.95 14.7001 36.51 14.7185 37.2218C14.7185 37.2218 14.7682 38.4648 15.1895 39.632C15.4015 40.2155 15.7024 40.7468 16.0452 41.0791C16.388 41.4115 16.7282 41.5946 17.3458 41.5946H25.7694C26.0834 41.5946 26.1095 41.558 26.2221 41.4586C26.3346 41.3591 26.5099 41.1053 26.7088 40.6945C26.9862 40.1161 27.3002 39.2604 27.8707 38.389L26.6669 37.3867V37.3841C26.3424 37.1146 26.1724 36.7011 26.209 36.2798C26.2482 35.8585 26.489 35.4843 26.858 35.2749L29.1713 33.9665L28.9358 33.1317H28.9331C28.9096 33.0454 28.8939 32.9564 28.886 32.8648C28.886 32.8648 28.8154 32.119 29.1582 31.3052C29.501 30.4914 30.5504 29.5388 31.9425 29.5388H31.9373C31.9373 29.5388 32.6517 29.4995 33.0442 29.3504C33.073 29.3399 33.0756 29.3347 33.0965 29.3242C33.0861 29.3033 33.0913 29.3216 33.0782 29.2928L29.6605 22.8605V22.8579C29.5088 22.57 29.4695 22.2351 29.5559 21.9211C30.2939 19.1603 29.5794 15.175 27.3315 11.9848C25.0836 8.79487 21.4331 6.36902 16.1787 6.36379C10.9632 6.36118 7.37797 8.98331 5.14312 12.3067C2.90833 15.6301 2.22006 19.7073 2.81146 21.9214C3.40549 24.1301 4.43916 25.7787 5.41785 27.3933C6.39654 29.008 7.38052 30.6487 7.38052 32.7213V42.6367H7.3779C7.3779 43.3459 6.80219 43.9243 6.09038 43.9243C5.3812 43.9243 4.80548 43.3459 4.80548 42.6367V32.7213C4.80548 31.4861 4.1748 30.3085 3.21703 28.7253C2.25663 27.1421 1.03195 25.2108 0.325386 22.5861L0.322769 22.5887V22.5861C-0.564339 19.2705 0.393425 14.7561 3.00506 10.8705C5.61668 6.98709 10.0787 3.78681 16.1784 3.78949L16.1787 3.79133Z"
          fill="black"
        />
        <path
          d="M46.713 34.5938C47.4248 34.5938 48.0005 35.1721 48.0005 35.8813C48.0005 36.5931 47.4248 37.1688 46.713 37.1688H42.314C41.6048 37.1688 41.0265 36.5931 41.0265 35.8813C41.0265 35.1721 41.6048 34.5938 42.314 34.5938H46.713Z"
          fill="black"
        />
        <path
          d="M44.6913 27.5601C45.1467 27.5627 45.5654 27.8087 45.793 28.2038C45.9631 28.4969 46.0102 28.8502 45.9212 29.1799C45.8323 29.5096 45.6177 29.7896 45.322 29.9597L41.5118 32.1606C40.8968 32.5165 40.1091 32.3045 39.7533 31.6895C39.5832 31.3938 39.5361 31.0432 39.625 30.7135C39.714 30.3837 39.9286 30.1011 40.2243 29.931L44.0345 27.7302V27.7328C44.236 27.6176 44.461 27.5574 44.6913 27.5601Z"
          fill="black"
        />
        <path
          d="M40.8733 39.4293C41.0957 39.4319 41.3182 39.4895 41.5118 39.602L45.322 41.8028C45.6177 41.9729 45.8323 42.2555 45.9212 42.5853C46.0102 42.9124 45.9631 43.2656 45.793 43.5613C45.4371 44.1763 44.6521 44.3857 44.0345 44.0324L40.2243 41.8316C39.9286 41.6615 39.714 41.3788 39.625 41.0491C39.5361 40.7194 39.5832 40.3687 39.7533 40.073C39.9835 39.6753 40.4101 39.4293 40.8733 39.4293Z"
          fill="black"
        />
      </svg>
    ),
  },
]

const COMMON_TASKS = [
  {
    task: "Pick a contact and make a call",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.38023 7.39225C5.72072 10.3112 8.10608 12.6318 11.0714 13.8892L11.0808 13.8932L11.6536 14.1485C12.3831 14.4736 13.2405 14.2427 13.7082 13.5954L14.664 12.2726C14.7224 12.1918 14.7073 12.0794 14.6298 12.0169L12.9612 10.6708C12.8783 10.6038 12.7563 10.6194 12.6928 10.7051L12.0434 11.5813C11.8857 11.7942 11.6009 11.8681 11.3596 11.7589C9.14228 10.7554 7.36049 8.97367 6.35705 6.75636C6.24784 6.51504 6.32178 6.23023 6.5346 6.07252L7.41088 5.42312C7.49651 5.35967 7.51209 5.2377 7.44517 5.15475L6.09889 3.48597C6.03632 3.40841 5.92401 3.39334 5.84321 3.45167L4.51324 4.41179C3.86165 4.88218 3.63218 5.74682 3.96478 6.4784L4.37979 7.39128C4.37993 7.3916 4.38008 7.39192 4.38023 7.39225ZM10.6275 14.923C7.40546 13.5553 4.81393 11.033 3.3574 7.86069L3.35652 7.85878L2.94065 6.944C2.38632 5.72468 2.76877 4.28363 3.85475 3.49964L5.18472 2.53952C5.75031 2.13122 6.53648 2.23667 6.97448 2.77959L8.32076 4.44837C8.78918 5.029 8.68008 5.8828 8.08071 6.32698L7.57858 6.69909C8.42677 8.34489 9.77105 9.68918 11.4168 10.5374L11.789 10.0352C12.2332 9.43586 13.0869 9.32676 13.6676 9.79518L15.3361 11.1413C15.8792 11.5794 15.9845 12.3659 15.5759 12.9315L14.6201 14.2543C13.8406 15.3331 12.4116 15.7178 11.1958 15.1762L10.6275 14.923Z"
          fill="#222222"
        />
      </svg>
    ),
    recommendation:
      "This task is usually one of the quickest things you can do on the move, but only if your car already knows who you're calling.",
    tip: "Save your regular contacts now, so you're saying a name instead of searching or dialling.",
  },
  {
    task: "Send a message",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14.9845 11.9302C14.9845 12.2476 14.8584 12.5521 14.6339 12.7765C14.4094 13.001 14.105 13.1271 13.7875 13.1271H5.90453C5.58711 13.1272 5.28272 13.2533 5.0583 13.4778L3.74048 14.7956C3.68105 14.855 3.60535 14.8955 3.52293 14.9119C3.44051 14.9283 3.35508 14.9199 3.27745 14.8877C3.19981 14.8556 3.13346 14.8011 3.08676 14.7312C3.04007 14.6614 3.01515 14.5792 3.01514 14.4952V4.74857C3.01514 4.43112 3.14124 4.12668 3.36571 3.90221C3.59018 3.67774 3.89462 3.55164 4.21207 3.55164H13.7875C14.105 3.55164 14.4094 3.67774 14.6339 3.90221C14.8584 4.12668 14.9845 4.43112 14.9845 4.74857V11.9302Z"
          stroke="black"
          strokeWidth="1.26343"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    recommendation:
      "Reading and replying to text messages can take real concentration, even with voice commands doing the work.",
    tip: "Turn on auto-read or voice-reply before you drive, or just let it wait until you've stopped.",
  },
  {
    task: "Navigate to a new address",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.1875 6.75C6.1875 5.1967 7.4467 3.9375 9 3.9375C10.5533 3.9375 11.8125 5.1967 11.8125 6.75C11.8125 8.3033 10.5533 9.5625 9 9.5625C7.4467 9.5625 6.1875 8.3033 6.1875 6.75ZM9 5.0625C8.06802 5.0625 7.3125 5.81802 7.3125 6.75C7.3125 7.68198 8.06802 8.4375 9 8.4375C9.93198 8.4375 10.6875 7.68198 10.6875 6.75C10.6875 5.81802 9.93198 5.0625 9 5.0625Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.09231 6.09543C4.29763 3.60452 6.37917 1.6875 8.87852 1.6875H9.12149C11.6208 1.6875 13.7024 3.60452 13.9077 6.09543C14.0178 7.43063 13.6053 8.75644 12.7573 9.79359L10.0612 13.0908C9.51271 13.7616 8.48729 13.7616 7.93884 13.0908L5.24274 9.79359C4.39468 8.75644 3.98225 7.43063 4.09231 6.09543ZM8.87852 2.8125C6.96465 2.8125 5.37073 4.28045 5.21351 6.18785C5.12739 7.23257 5.4501 8.26994 6.11366 9.08146L8.80976 12.3787C8.90808 12.499 9.09192 12.499 9.19025 12.3787L11.8863 9.08146C12.5499 8.26994 12.8726 7.23257 12.7865 6.18785C12.6293 4.28045 11.0354 2.8125 9.12149 2.8125H8.87852Z"
          fill="#222222"
        />
        <path
          d="M5.75312 12.2516C5.89205 11.9737 5.77942 11.6358 5.50156 11.4969C5.2237 11.358 4.88582 11.4706 4.74689 11.7484L3.24689 14.7484C3.1597 14.9228 3.16902 15.1299 3.27151 15.2957C3.374 15.4616 3.55505 15.5625 3.75 15.5625H14.25C14.445 15.5625 14.626 15.4616 14.7285 15.2957C14.831 15.1299 14.8403 14.9228 14.7531 14.7484L13.2531 11.7484C13.1142 11.4706 12.7763 11.358 12.4984 11.4969C12.2206 11.6358 12.108 11.9737 12.2469 12.2516L13.3399 14.4375H4.66014L5.75312 12.2516Z"
          fill="#222222"
        />
      </svg>
    ),
    recommendation:
      "This task is one of the most demanding things any in-car system asks of you, mixing searching, spatial thinking and reading a screen all at once.",
    tip: "Enter your destination before you start driving, or save your regular addresses as favourites.",
  },
  {
    task: "Play a song",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.10522 12.7895V4.57895L14.6842 3.3158V11.5263"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.21041 14.6842C6.25684 14.6842 7.10515 13.8359 7.10515 12.7895C7.10515 11.743 6.25684 10.8947 5.21041 10.8947C4.16398 10.8947 3.31567 11.743 3.31567 12.7895C3.31567 13.8359 4.16398 14.6842 5.21041 14.6842Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.7895 13.4205C13.8359 13.4205 14.6842 12.5722 14.6842 11.5257C14.6842 10.4793 13.8359 9.63098 12.7895 9.63098C11.7431 9.63098 10.8948 10.4793 10.8948 11.5257C10.8948 12.5722 11.7431 13.4205 12.7895 13.4205Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    recommendation:
      "Setting things up before you drive like your contacts, your playlist and your destination is far more reliable than trying to manage distraction once you're already moving. Let's cut the distractions, use our cars' tech smartly, and get home safely — every trip, every time.",
    tip: "",
  },
  {
    task: "Tuning a radio station",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.10522 12.7895V4.57895L14.6842 3.3158V11.5263"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.21041 14.6842C6.25684 14.6842 7.10515 13.8359 7.10515 12.7895C7.10515 11.743 6.25684 10.8947 5.21041 10.8947C4.16398 10.8947 3.31567 11.743 3.31567 12.7895C3.31567 13.8359 4.16398 14.6842 5.21041 14.6842Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.7895 13.4205C13.8359 13.4205 14.6842 12.5722 14.6842 11.5257C14.6842 10.4793 13.8359 9.63098 12.7895 9.63098C11.7431 9.63098 10.8948 10.4793 10.8948 11.5257C10.8948 12.5722 11.7431 13.4205 12.7895 13.4205Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    recommendation:
      "Changing station sounds simple, but scanning through options means more fiddling than you'd expect.",
    tip: "Save your go-to stations as presets so your favourite is one press away.",
  },
]

const DISCONNECTED_TASKS = [
  {
    task: "Setting a destination",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.1875 6.75C6.1875 5.1967 7.4467 3.9375 9 3.9375C10.5533 3.9375 11.8125 5.1967 11.8125 6.75C11.8125 8.3033 10.5533 9.5625 9 9.5625C7.4467 9.5625 6.1875 8.3033 6.1875 6.75ZM9 5.0625C8.06802 5.0625 7.3125 5.81802 7.3125 6.75C7.3125 7.68198 8.06802 8.4375 9 8.4375C9.93198 8.4375 10.6875 7.68198 10.6875 6.75C10.6875 5.81802 9.93198 5.0625 9 5.0625Z"
          fill="#222222"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.09231 6.09543C4.29763 3.60452 6.37917 1.6875 8.87852 1.6875H9.12149C11.6208 1.6875 13.7024 3.60452 13.9077 6.09543C14.0178 7.43063 13.6053 8.75644 12.7573 9.79359L10.0612 13.0908C9.51271 13.7616 8.48729 13.7616 7.93884 13.0908L5.24274 9.79359C4.39468 8.75644 3.98225 7.43063 4.09231 6.09543ZM8.87852 2.8125C6.96465 2.8125 5.37073 4.28045 5.21351 6.18785C5.12739 7.23257 5.4501 8.26994 6.11366 9.08146L8.80976 12.3787C8.90808 12.499 9.09192 12.499 9.19025 12.3787L11.8863 9.08146C12.5499 8.26994 12.8726 7.23257 12.7865 6.18785C12.6293 4.28045 11.0354 2.8125 9.12149 2.8125H8.87852Z"
          fill="#222222"
        />
        <path
          d="M5.75312 12.2516C5.89205 11.9737 5.77942 11.6358 5.50156 11.4969C5.2237 11.358 4.88582 11.4706 4.74689 11.7484L3.24689 14.7484C3.1597 14.9228 3.16902 15.1299 3.27151 15.2957C3.374 15.4616 3.55505 15.5625 3.75 15.5625H14.25C14.445 15.5625 14.626 15.4616 14.7285 15.2957C14.831 15.1299 14.8403 14.9228 14.7531 14.7484L13.2531 11.7484C13.1142 11.4706 12.7763 11.358 12.4984 11.4969C12.2206 11.6358 12.108 11.9737 12.2469 12.2516L13.3399 14.4375H4.66014L5.75312 12.2516Z"
          fill="#222222"
        />
      </svg>
    ),
    recommendation:
      "This task is one of the most demanding things any in-car system asks of you, mixing searching, spatial thinking and reading a screen all at once.",
    tip: "Enter your destination before you start driving, or save your regular addresses as favourites.",
  },
  {
    task: "Tuning a radio station",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.10522 12.7895V4.57895L14.6842 3.3158V11.5263"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.21041 14.6842C6.25684 14.6842 7.10515 13.8359 7.10515 12.7895C7.10515 11.743 6.25684 10.8947 5.21041 10.8947C4.16398 10.8947 3.31567 11.743 3.31567 12.7895C3.31567 13.8359 4.16398 14.6842 5.21041 14.6842Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.7895 13.4205C13.8359 13.4205 14.6842 12.5722 14.6842 11.5257C14.6842 10.4793 13.8359 9.63098 12.7895 9.63098C11.7431 9.63098 10.8948 10.4793 10.8948 11.5257C10.8948 12.5722 11.7431 13.4205 12.7895 13.4205Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    recommendation:
      "Changing station sounds simple, but scanning through options means more fiddling than you'd expect.",
    tip: "Save your go-to stations as presets so your favourite is one press away.",
  },
  {
    task: "Change the radio station",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.10522 12.7895V4.57895L14.6842 3.3158V11.5263"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.21041 14.6842C6.25684 14.6842 7.10515 13.8359 7.10515 12.7895C7.10515 11.743 6.25684 10.8947 5.21041 10.8947C4.16398 10.8947 3.31567 11.743 3.31567 12.7895C3.31567 13.8359 4.16398 14.6842 5.21041 14.6842Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.7895 13.4205C13.8359 13.4205 14.6842 12.5722 14.6842 11.5257C14.6842 10.4793 13.8359 9.63098 12.7895 9.63098C11.7431 9.63098 10.8948 10.4793 10.8948 11.5257C10.8948 12.5722 11.7431 13.4205 12.7895 13.4205Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    recommendation: "Scanning stations demands real attention.",
    tip: "Save your favourites as presets before heading off, one press beats a search.",
  },
  {
    task: "Change the temperature",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.66667 13.3334L5.83333 11.6667L4 12" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
        <path
          d="M6.66667 2.66669L5.83333 4.33335L4 4.00002"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M7.05675 10H6.66675" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.33325 8H5.66659L6.66659 6" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
        <path
          d="M13.3333 9.69331C13.8416 9.98683 14.239 10.4399 14.4636 10.9822C14.6882 11.5246 14.7277 12.1259 14.5757 12.6929C14.4238 13.2599 14.089 13.761 13.6233 14.1183C13.1576 14.4757 12.5869 14.6694 11.9999 14.6694C11.4129 14.6694 10.8423 14.4757 10.3766 14.1183C9.91084 13.761 9.57605 13.2599 9.42412 12.6929C9.27218 12.1259 9.3116 11.5246 9.53624 10.9822C9.76089 10.4399 10.1582 9.98683 10.6666 9.69331V2.66665C10.6666 2.31302 10.8071 1.97389 11.0571 1.72384C11.3072 1.47379 11.6463 1.33331 11.9999 1.33331C12.3535 1.33331 12.6927 1.47379 12.9427 1.72384C13.1928 1.97389 13.3333 2.31302 13.3333 2.66665V9.69331Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2.66675 6.66669L3.66675 8.00002L2.66675 9.33335"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M4.66675 14L6.66675 10L5.66675 8" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4.66675 2L6.66675 6H8.00008" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    recommendation: "Screens can hold your eyes longer than they should.",
    tip: "If you can use a physical volume knob or steering-wheel control so you don't need to look away.",
  },
  {
    task: "Adjust the volume",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.10522 12.7895V4.57895L14.6842 3.3158V11.5263"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.21041 14.6842C6.25684 14.6842 7.10515 13.8359 7.10515 12.7895C7.10515 11.743 6.25684 10.8947 5.21041 10.8947C4.16398 10.8947 3.31567 11.743 3.31567 12.7895C3.31567 13.8359 4.16398 14.6842 5.21041 14.6842Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.7895 13.4205C13.8359 13.4205 14.6842 12.5722 14.6842 11.5257C14.6842 10.4793 13.8359 9.63098 12.7895 9.63098C11.7431 9.63098 10.8948 10.4793 10.8948 11.5257C10.8948 12.5722 11.7431 13.4205 12.7895 13.4205Z"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    recommendation: "A single touch beats a menu.",
    tip: "If you can use a physical volume knob or steering-wheel control so you don't need to look away.",
  },
]

const HOW_TO = [
  "Open your car’s Bluetooth or projection menu",
  "Pair or select your phone",
  "Confirm on both devices",
  "Return to this test",
]

const SUGGESTED = ["Acknowledge a job", "Accept a call out", "Call home base", "Call the customer", "Delete a job"]

const TASK_QUESTIONS = [
  {
    issue: "The system misunderstood me",
    recommendation:
      "When voice commands aren't understood, you end up repeating yourself and every repeat is extra time with less attention on the road",
    tip: "Try to speak clearly right after the prompt. If voice keeps missing on a particular task, switch to a screen or button shortcut instead",
  },
  {
    issue: "The screen lagged",
    recommendation: "A slow-responding screen can hold your attention longer than the task itself should.",
    tip: "Handle touch-based tasks before you set off, or use voice or physical controls as a backup if the screen's being sluggish.",
  },
  {
    issue: "A tap did not register",
    recommendation:
      "A tap that doesn't register means doing it again adding more time and more attention pulled off the road.",
    tip: "Where the system allows it, a physical button or voice command can be a more reliable option for that task.",
  },
  {
    issue: "A physical button or control did not respond",
    recommendation: "An unresponsive control means extra, unplanned steps for something that should be simple.",
    tip: "If it keeps happening, it's worth reporting so it can be checked — this is more likely a system fault than a driving habit to fix.",
  },
  {
    issue: "I had to repeat a command",
    recommendation: "Repeating a command adds an extra step and extra steps mean more time with your attention split.",
    tip: "If a task usually takes more than one attempt, it's worth setting up before you drive rather than working through it on the move.",
  },
  {
    issue: "I had to switch between touch, press and voice",
    recommendation: "Jumping between touch, buttons and voice partway through one task  can add complexity.",
    tip: "Pick one way of completing a task to help reduce the cognitive load.",
  },
]

// Steps -> eyes-off-road time / DRT miss rate / star rating reference table.
export const DISTRACTION_TABLE = [
  { steps: 1, stars: 5, eyesOffMs: 928, eyesOffS: 0.93, drtMissRate: 10.2 },
  { steps: 2, stars: 5, eyesOffMs: 1248, eyesOffS: 1.25, drtMissRate: 11.3 },
  { steps: 3, stars: 4, eyesOffMs: 1617, eyesOffS: 1.62, drtMissRate: 12.4 },
  { steps: 4, stars: 4, eyesOffMs: 2033, eyesOffS: 2.03, drtMissRate: 13.5 },
  { steps: 5, stars: 3, eyesOffMs: 2496, eyesOffS: 2.5, drtMissRate: 14.6 },
  { steps: 6, stars: 3, eyesOffMs: 3007, eyesOffS: 3.01, drtMissRate: 15.7 },
  { steps: 7, stars: 2, eyesOffMs: 3566, eyesOffS: 3.57, drtMissRate: 16.8 },
  { steps: 8, stars: 2, eyesOffMs: 4172, eyesOffS: 4.17, drtMissRate: 17.9 },
  { steps: 9, stars: 1, eyesOffMs: 4825, eyesOffS: 4.83, drtMissRate: 19.0 },
  { steps: 10, stars: 1, eyesOffMs: 5527, eyesOffS: 5.53, drtMissRate: 20.1 },
  { steps: 11, stars: 0, eyesOffMs: 6276, eyesOffS: 6.28, drtMissRate: 21.2 },
  { steps: 12, stars: 0, eyesOffMs: 7072, eyesOffS: 7.07, drtMissRate: 22.3 },
]

const DISTRACTION_LABELS: Record<number, string> = {
  5: "Very low distraction",
  4: "Low distraction",
  3: "Moderate distraction",
  2: "High distraction",
  1: "Very high distraction",
  0: "Unacceptable while driving",
}

const DISTRACTION_STAR_FILL: Record<number, string> = {
  5: "green-mid",
  4: "green-light",
  3: "amber-mid",
  2: "red-light",
  1: "red",
}

const DISTRACTION_TEXT_FILL: Record<number, string> = {
  5: "text-green",
  4: "text-green",
  3: "text-amber",
  2: "text-red",
  1: "text-red-light",
}

const COUNTER_COLORS: Record<string, string> = {
  touch: "bg-[#A2E0EE99]",
  press: "bg-[#AD5FDC99]",
  voice: "bg-[#607CDE99]",
}

// Distance covered at 50km/h over the eyes-off-road duration, in metres per second.
const KPH_50_IN_MPS = (50 * 1000) / 3600

// Steps are clamped into the reference table's 1-12 range (steps beyond 12 use row 12's values).
const getDistractionForSteps = (steps: number) => {
  const row = DISTRACTION_TABLE[Math.min(Math.max(steps, 1), DISTRACTION_TABLE.length) - 1]
  const stars = row.stars
  const label = DISTRACTION_LABELS[stars]
  // 0-star tier has no dedicated colour (it's "all grey" for the stars); fall back to the most severe defined tier.
  const starFill = DISTRACTION_STAR_FILL[stars] ?? DISTRACTION_STAR_FILL[1]
  const textFill = DISTRACTION_TEXT_FILL[stars] ?? DISTRACTION_TEXT_FILL[1]
  const eyesOffRoadSeconds = row.eyesOffS
  const drivingDistanceMeters = KPH_50_IN_MPS * eyesOffRoadSeconds
  return { row, stars, label, starFill, textFill, eyesOffRoadSeconds, drivingDistanceMeters }
}
